import { useEffect, useState } from 'react';
import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, FormHelperText, Snackbar, Alert,
  CircularProgress, Backdrop, Autocomplete, Checkbox, Chip,
  InputAdornment, Pagination, Collapse, Tooltip
} from '@mui/material';
import { Close, Add, Delete, ShoppingCart, Visibility, CheckCircle, Warning, List, Search, PictureAsPdf, WhatsApp, Print, ExpandMore, ExpandLess, PersonAdd, AddBox } from '@mui/icons-material';
import { downloadBillPDF, printBillPDF, shareViaWhatsApp } from './BillPdfGenerator';
import ClientModal from '../Master/ClientModal';
import ItemModal from '../ProductItem/ItemModal';

const SaleModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  saleDetails,
  setSaleDetails,
  relatedItems: relatedItemsProp,
  setRelatedItems: setRelatedItemsProp,
  onSave,
  onVerify,
  customers,
  items,
  banks,
  staffList,
  loading,
  freightPackingGST,
  companyId,
  branchId,
  token
}) => {
  const { userId } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Changed from 'sm' to 'md' for better mobile detection
  const [itemsModalOpen, setItemsModalOpen] = useState(false);

  // Quick Add Client state
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientFormData, setClientFormData] = useState({});
  const [clientLoading, setClientLoading] = useState(false);

  // Quick Add Item state
  const [quickItemModalOpen, setQuickItemModalOpen] = useState(false);
  const [quickItemCategories, setQuickItemCategories] = useState([]);
  const [quickItemBrands, setQuickItemBrands] = useState([]);
  const [quickItemUoms, setQuickItemUoms] = useState([]);

  // Get Items modal state
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [getItemsLoading, setGetItemsLoading] = useState(false);
  const [addingItemsLoading, setAddingItemsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearchValue, setGetItemsSearchValue] = useState('');
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // Related items state for products
  const [relatedItems, setRelatedItems] = useState(relatedItemsProp || []);
  const [relatedItemsLoading, setRelatedItemsLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [initialRelatedItemsLoaded, setInitialRelatedItemsLoaded] = useState(false);

  // Sync relatedItems with parent component (push up)
  useEffect(() => {
    if (setRelatedItemsProp) {
      setRelatedItemsProp(relatedItems);
    }
  }, [relatedItems]);

  // Sync relatedItems from parent prop (pull down) - when API data arrives
  useEffect(() => {
    if ((modalAction === 'edit' || modalAction === 'view') && relatedItemsProp && relatedItemsProp.length > 0) {
      setRelatedItems(relatedItemsProp);
      setInitialRelatedItemsLoaded(true);
    }
  }, [relatedItemsProp]);

  // Reset initialRelatedItemsLoaded when modal opens
  useEffect(() => {
    if (open) {
      setInitialRelatedItemsLoaded(false);
    }
  }, [open]);

  // Ledger modal state
  const [ledgerModalOpen, setLedgerModalOpen] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerFromDate, setLedgerFromDate] = useState('');
  const [ledgerToDate, setLedgerToDate] = useState('');

  // New state for additional dropdowns
  const [states, setStates] = useState([]);
  const salesmen = staffList || [];
  const getSalesmanName = (id) => {
    const s = salesmen.find(x => (x.MasterId || x.Id) === id);
    return s ? (s.MasterName || s.Name || `${s.FirstName || ''} ${s.LastName || ''}`.trim()) : '';
  };
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [customerClosingBalance, setCustomerClosingBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [customerAdvanceAmount, setCustomerAdvanceAmount] = useState(null);
  const [loadingAdvance, setLoadingAdvance] = useState(false);
  
  // Currency dropdown state
  const [currencies, setCurrencies] = useState([]);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  // GstCalculationType: 1 = Excluding Tax, 2 = Including Tax - REMOVED

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // PDF and WhatsApp state
  const [pdfLoading, setPdfLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);

  // Terms & Conditions state
  const [tcData, setTcData] = useState([]);
  const [tcLoading, setTcLoading] = useState(false);
  const [selectedTerms, setSelectedTerms] = useState([]);
  const [tcExpanded, setTcExpanded] = useState(false);
  const [tcSearchValue, setTcSearchValue] = useState('');

  // Cancel confirmation modal state
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Sale' : modalAction === 'edit' ? 'Edit Sale' : 'View Sale';

  // Sync selectedTerms when modal opens with existing terms data
  useEffect(() => {
    if (open && formData.SelectedTerms && formData.SelectedTerms.length > 0) {
      setSelectedTerms(formData.SelectedTerms);
    } else if (open) {
      setSelectedTerms([]);
    }
  }, [open, formData.SelectedTerms]);

  // Reset errors when modal opens/closes
  useEffect(() => {
    if (open) {
      console.log('Modal opened with formData:', {
        modalAction,
        DispatchFrom: formData.DispatchFrom,
        ShipTo: formData.ShipTo,
        ShipGSTNo: formData.ShipGSTNo,
        ShipToAddress1: formData.ShipToAddress1
      });

      setErrors({});
      setTouched({});
      setSnackbar({ open: false, message: '', severity: 'error' });
      setHasUnsavedChanges(false);
      setCancelConfirmOpen(false);
      setCustomerClosingBalance(null); // Clear closing balance when modal opens
      setCustomerAdvanceAmount(null); // Clear advance amount when modal opens

      // Fetch dropdown data and branch details without loading
      const fetchAllDropdownData = async () => {
        try {
          // Fetch all data in parallel
          await Promise.all([
            fetchStates(),
            fetchCurrencies(),
            fetchTermsAndConditions()
          ]);

          // Only auto-populate dispatch details for create mode
          if (modalAction === 'create') {
            const branchDetails = await fetchBranchDetails();
            setFormData(prev => ({
              ...prev,
              DispatchFrom: branchDetails.branchName,
              DispatchLocation: branchDetails.location,
              DispatchAddress1: branchDetails.addressLine1,
              DispatchAddress2: branchDetails.addressLine2,
              DispatchPincode: branchDetails.pincode,
              TransporterName: branchDetails.companyName,
              TransporterVAT: branchDetails.VATNumber
            }));
          }
          // For edit/view mode, formData is already set from SaleIndex with all fields

          // Fetch customer ledger balance for edit/view mode
          if ((modalAction === 'edit' || modalAction === 'view') && formData.MasterId) {
            fetchCustomerLedgerDetails(formData.MasterId);
            fetchCustomerAdvanceDetails(formData.MasterId);
          }
        } catch (error) {
          console.error('Error fetching dropdown data:', error);
        }
      };

      fetchAllDropdownData();
    }
  }, [open, modalAction]);

  // Fetch States
  const fetchStates = async () => {
    try {
      const response = await fetchData('SaleApi', `/StateCode/${companyId}/${branchId}`, token);
      setStates(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    }
  };

  // Fetch Currencies
  const fetchCurrencies = async () => {
    setCurrencyLoading(true);
    try {
      const response = await fetchData('CurrencyTypeApi', `/${companyId}`, token);
      if (Array.isArray(response)) {
        setCurrencies(response);
      } else if (response?.response_data && Array.isArray(response.response_data)) {
        setCurrencies(response.response_data);
      } else {
        setCurrencies([]);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setCurrencies([]);
    } finally {
      setCurrencyLoading(false);
    }
  };

  // Fetch Terms & Conditions
  const fetchTermsAndConditions = async () => {
    if (!companyId || !branchId || !token) return;
    setTcLoading(true);
    try {
      const response = await fetchData('SaleApi', `/SaleTerms/${companyId}/${branchId}`, token);
      const termsData = response.response_data || response || [];
      setTcData(Array.isArray(termsData) ? termsData : []);
      setSelectedTerms(formData.SelectedTerms || []);
    } catch (error) {
      console.error('Error fetching terms and conditions:', error);
      setTcData([]);
    } finally {
      setTcLoading(false);
    }
  };

  const handleToggleTerm = (termId) => {
    setSelectedTerms(prev => {
      const newSelection = prev.includes(termId) 
        ? prev.filter(id => id !== termId)
        : [...prev, termId];
      
      const selectedTermsData = tcData.filter(term => newSelection.includes(term.Id));
      setFormData(prevForm => ({ 
        ...prevForm, 
        SelectedTerms: newSelection,
        SelectedTermsData: selectedTermsData,
        TermsAndConditions: selectedTermsData.map(t => t.Name).join('\n')
      }));
      
      return newSelection;
    });
  };

  const handleSelectAllTerms = () => {
    let newSelection;
    if (selectedTerms.length === tcData.length) {
      newSelection = [];
    } else {
      newSelection = tcData.map(term => term.Id);
    }
    setSelectedTerms(newSelection);
    
    const selectedTermsData = tcData.filter(term => newSelection.includes(term.Id));
    setFormData(prev => ({ 
      ...prev, 
      SelectedTerms: newSelection,
      SelectedTermsData: selectedTermsData,
      TermsAndConditions: selectedTermsData.map(t => t.Name).join('\n')
    }));
  };

  // Fetch branch details for dispatch section
  const fetchBranchDetails = async () => {
    try {
      const response = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      if (response.responseCode == '1111') {
        const branchData = response.response_data || {};
        return {
          branchName: branchData.Name || '',
          location: branchData.Location || '',
          addressLine1: branchData.AddressLine1 || '',
          addressLine2: branchData.AddressLine2 || '',
          pincode: branchData.PinCode || '',
          companyName: branchData.CompanyName || branchData.Name || '',
          VATNumber: branchData.VATNumber || branchData.CompanyVATNumber || ''
        };
      }
      return { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', VATNumber: '' };
    } catch (error) {
      console.error('Error fetching Branch Details:', error);
      return { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', VATNumber: '' };
    }
  };

  // Fetch customer details for shipping section using ClientApi
  const fetchSingleClient = async (id) => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${id}`, token);
      return response.response_data.Ledger || response || null;
    } catch (error) {
      console.error('Error fetching client:', error);
      return null;
    }
  };

  // Fetch customer ledger details for closing balance
  const fetchCustomerLedgerDetails = async (masterId) => {
    if (!masterId) {
      setCustomerClosingBalance(null);
      return;
    }

    setLoadingBalance(true);
    try {
      const fromDate = formData.BillDate || new Date().toISOString().split('T')[0];
      const toDate = formData.BillDate || new Date().toISOString().split('T')[0];

      const response = await fetchData('SaleApi', `/MasterLedgerDetails/${companyId}/${masterId}/${companyId}/${fromDate}/${toDate}`, token);
      const responseData = response.response_data || response || [];

      // Handle array response - get the first item's Amount
      let closingBalance = 0;
      if (Array.isArray(responseData) && responseData.length > 0) {
        closingBalance = parseFloat(responseData[0].Amount) || 0;
      } else if (responseData.Amount !== undefined) {
        closingBalance = parseFloat(responseData.Amount) || 0;
      }

      setCustomerClosingBalance(closingBalance);
    } catch (error) {
      console.error('Error fetching customer ledger details:', error);
      setCustomerClosingBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  // Fetch customer advance details
  const fetchCustomerAdvanceDetails = async (masterId) => {
    if (!masterId) {
      setCustomerAdvanceAmount(null);
      return;
    }

    setLoadingAdvance(true);
    try {

      const fromDate = formData.BillDate || new Date().toISOString().split('T')[0];
      const toDate = formData.BillDate || new Date().toISOString().split('T')[0];

      const response = await fetchData('SaleApi', `/CustomerAdvanceDetails/${companyId}/${branchId}/${masterId}/${fromDate}/${toDate}`, token);
      const responseData = response.response_data || response || [];

      let advanceAmount = 0;
      if (Array.isArray(responseData) && responseData.length > 0) {
        advanceAmount = parseFloat(responseData[0].ClosingAdvance) || 0;
      } else if (responseData.ClosingAdvance !== undefined) {
        advanceAmount = parseFloat(responseData.ClosingAdvance) || 0;
      }

      setCustomerAdvanceAmount(advanceAmount);
    } catch (error) {
      console.error('Error fetching customer advance details:', error);
      setCustomerAdvanceAmount(null);
    } finally {
      setLoadingAdvance(false);
    }
  };

  // Fetch customer details for shipping section using ClientApi
  const fetchCustomerDetails = async (masterId) => {
    try {
      const customerData = await fetchSingleClient(masterId);
      if (customerData) {
        return {
          customerName: customerData.CustomerName || customerData.Name || '',
          VATNumber: customerData.VATNumber || '',
          addressLine1: customerData.AddressLine1 || '',
          addressLine2: customerData.AddressLine2 || '',
          location: customerData.Location || '',
          pincode: customerData.PinCode || '',
          CurrencyTypeId: customerData.CurrencyTypeId || null,
          CurrencyName: customerData.CurrencyName || '',
          CreditDays: customerData.CreditDays ?? 0
        };
      }
      return { customerName: '', VATNumber: '', addressLine1: '', addressLine2: '', location: '', pincode: '', CurrencyTypeId: null, CurrencyName: '', CreditDays: 0 };
    } catch (error) {
      console.error('Error fetching customer details:', error);
      return { customerName: '', VATNumber: '', addressLine1: '', addressLine2: '', location: '', pincode: '', CurrencyTypeId: null, CurrencyName: '', CreditDays: 0 };
    }
  };

  // Helper: compute DueDate from a base date and credit days
  const computeDueDate = (billDate, creditDays) => {
    if (!billDate) return '';
    const days = parseInt(creditDays) || 0;
    if (days === 0) return billDate;
    const base = new Date(billDate);
    base.setDate(base.getDate() + days);
    const yyyy = base.getFullYear();
    const mm = String(base.getMonth() + 1).padStart(2, '0');
    const dd = String(base.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Fetch bill number when customer changes or on create
  const fetchBillNumber = async () => {
    if (modalAction !== 'create') return;

    try {
      const response = await fetchData('SaleApi', `/MaxBillno/${companyId}/${branchId}/0`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';

      setFormData(prev => ({
        ...prev,
        BillNumber: billNumber,
        BillPrefix: billPrefix,
        FinYear: finYear
      }));
    } catch (error) {
      console.error('Error fetching bill number:', error);
    }
  };

  const showError = (message) => {
    setSnackbar({ open: true, message, severity: 'error' });
  };

  const showSuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' });
  };

  // Handle PDF Download
  const handleDownloadPDF = async () => {
    if (!formData.BillNumber) {
      showError('Please save the sale first before generating PDF');
      return;
    }

    setPdfLoading(true);
    try {
      // Fetch branch details for company information
      const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const branchData = branchResponse.responseCode == '1111' ? (branchResponse.response_data || {}) : {};

      const companyDetails = {
        companyName: branchData.CompanyName || branchData.Name || 'BPS GLOBAL TRADING LLC',
        address: `${branchData.AddressLine1 || ''} ${branchData.AddressLine2 || ''}, ${branchData.Location || 'Dubai'}, UAE`.trim(),
        trn: branchData.VATNumber || branchData.CompanyVATNumber || 'TRN-XXXXXXXXX',
        phone: branchData.Phone || branchData.Mobile || '+971-XX-XXXXXXX',
        email: branchData.Email || 'info@company.ae',
        vatNumber: branchData.VATNumber || branchData.CompanyVATNumber || ''
      };

      // Fetch fresh sale data to get terms
      let termsForPDF = formData.SelectedTermsData || [];
      // Use tcData + selectedTerms from modal state if available
      if (termsForPDF.length === 0 && selectedTerms.length > 0 && tcData.length > 0) {
        termsForPDF = tcData.filter(t => selectedTerms.includes(t.Id)).map(t => ({ Id: t.Id, Name: t.Name }));
      }
      if (formData.Id && termsForPDF.length === 0) {
        try {
          const freshSale = await fetchData('SaleApi', `/${formData.Id}`, token);
          const freshSaleData = freshSale.response_data || freshSale || {};
          const termsFromApi = freshSaleData.SalesTermsDetails || freshSaleData.SaleTermsDetails || [];
          if (termsFromApi.length > 0) {
            termsForPDF = termsFromApi.map(t => ({ Id: t.TermsAndConditionId || t.Id, Name: t.Name || t.TermsAndCondition?.Name || tcData.find(tc => tc.Id === (t.TermsAndConditionId || t.Id))?.Name || '' }));
          }
        } catch (e) { console.error('Error fetching terms for PDF:', e); }
      }

      const saleDataForPDF = {
        ...formData,
        saleDetails: saleDetails,
        CustomerName: formData.CustomerName || getCustomerDisplayName(),
        VATNumber: formData.VATNumber || getSelectedCustomerGST(),
        SalesmanName: getSalesmanName(formData.SalesManId),
        SelectedTermsData: termsForPDF
      };

      await downloadBillPDF(saleDataForPDF, companyDetails, items, banks);
      showSuccess('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  // Handle Print PDF
  const handlePrintPDF = async () => {
    if (!formData.BillNumber) {
      showError('Please save the sale first before printing');
      return;
    }

    setPrintLoading(true);
    try {
      // Fetch branch details for company information
      const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const branchData = branchResponse.responseCode == '1111' ? (branchResponse.response_data || {}) : {};

      const companyDetails = {
        companyName: branchData.CompanyName || branchData.Name || 'BPS GLOBAL TRADING LLC',
        address: `${branchData.AddressLine1 || ''} ${branchData.AddressLine2 || ''}, ${branchData.Location || 'Dubai'}, UAE`.trim(),
        trn: branchData.VATNumber || branchData.CompanyVATNumber || 'TRN-XXXXXXXXX',
        phone: branchData.Phone || branchData.Mobile || '+971-XX-XXXXXXX',
        email: branchData.Email || 'info@company.ae',
        vatNumber: branchData.VATNumber || branchData.CompanyVATNumber || ''
      };

      // Fetch fresh sale data to get terms
      let termsForPDF = formData.SelectedTermsData || [];
      // Use tcData + selectedTerms from modal state if available
      if (termsForPDF.length === 0 && selectedTerms.length > 0 && tcData.length > 0) {
        termsForPDF = tcData.filter(t => selectedTerms.includes(t.Id)).map(t => ({ Id: t.Id, Name: t.Name }));
      }
      // Fallback: fetch from API if still empty
      if (formData.Id && termsForPDF.length === 0) {
        try {
          const freshSale = await fetchData('SaleApi', `/${formData.Id}`, token);
          const freshSaleData = freshSale.response_data || freshSale || {};
          console.log('Print - freshSaleData keys:', Object.keys(freshSaleData));
          console.log('Print - freshSaleData full:', freshSaleData);
          const termsFromApi = freshSaleData.SalesTermsDetails || freshSaleData.SaleTermsDetails || [];
          console.log('Print - termsFromApi:', termsFromApi);
          if (termsFromApi.length > 0) {
            termsForPDF = termsFromApi.map(t => ({ Id: t.TermsAndConditionId || t.Id, Name: t.Name || t.TermName || t.TermsAndCondition?.Name || tcData.find(tc => tc.Id === (t.TermsAndConditionId || t.Id))?.Name || '' }));
          }
        } catch (e) { console.error('Error fetching terms for PDF:', e); }
      }
      console.log('Print - final termsForPDF:', termsForPDF);

      const saleDataForPDF = {
        ...formData,
        saleDetails: saleDetails,
        CustomerName: formData.CustomerName || getCustomerDisplayName(),
        VATNumber: formData.VATNumber || getSelectedCustomerGST(),
        SalesmanName: getSalesmanName(formData.SalesManId),
        SelectedTermsData: termsForPDF
      };

      await printBillPDF(saleDataForPDF, companyDetails, items, banks);
      showSuccess('PDF opened in new window. Print dialog should appear automatically.');
    } catch (error) {
      console.error('Error printing PDF:', error);
      showError('Failed to print PDF. Please try again.');
    } finally {
      setPrintLoading(false);
    }
  };

  // Handle WhatsApp Share
  const handleWhatsAppShare = async () => {
    if (!formData.BillNumber) {
      showError('Please save the sale first before sharing');
      return;
    }

    setWhatsappLoading(true);
    try {
      // Fetch branch details for company information
      const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
      const branchData = branchResponse.responseCode == '1111' ? (branchResponse.response_data || {}) : {};

      const companyDetails = {
        companyName: branchData.CompanyName || branchData.Name || 'BPS GLOBAL TRADING LLC',
        address: `${branchData.AddressLine1 || ''} ${branchData.AddressLine2 || ''}, ${branchData.Location || 'Dubai'}, UAE`.trim(),
        trn: branchData.VATNumber || branchData.CompanyVATNumber || 'TRN-XXXXXXXXX',
        phone: branchData.Phone || branchData.Mobile || '+971-XX-XXXXXXX',
        email: branchData.Email || 'info@company.ae',
        vatNumber: branchData.VATNumber || branchData.CompanyVATNumber || ''
      };

      // Fetch fresh sale data to get terms
      let termsForPDF = formData.SelectedTermsData || [];
      // Use tcData + selectedTerms from modal state if available
      if (termsForPDF.length === 0 && selectedTerms.length > 0 && tcData.length > 0) {
        termsForPDF = tcData.filter(t => selectedTerms.includes(t.Id)).map(t => ({ Id: t.Id, Name: t.Name }));
      }
      if (formData.Id && termsForPDF.length === 0) {
        try {
          const freshSale = await fetchData('SaleApi', `/${formData.Id}`, token);
          const freshSaleData = freshSale.response_data || freshSale || {};
          const termsFromApi = freshSaleData.SalesTermsDetails || freshSaleData.SaleTermsDetails || [];
          if (termsFromApi.length > 0) {
            termsForPDF = termsFromApi.map(t => ({ Id: t.TermsAndConditionId || t.Id, Name: t.Name || t.TermsAndCondition?.Name || tcData.find(tc => tc.Id === (t.TermsAndConditionId || t.Id))?.Name || '' }));
          }
        } catch (e) { console.error('Error fetching terms for WhatsApp:', e); }
      }

      const saleDataForPDF = {
        ...formData,
        saleDetails: saleDetails,
        CustomerName: formData.CustomerName || getCustomerDisplayName(),
        VATNumber: formData.VATNumber || getSelectedCustomerGST(),
        SalesmanName: getSalesmanName(formData.SalesManId),
        SelectedTermsData: termsForPDF
      };

      // Get customer phone number (you may need to add this field to your customer data)
      const customerPhone = formData.CustomerPhone || '';

      const result = await shareViaWhatsApp(saleDataForPDF, companyDetails, items, customerPhone, banks);

      if (result.success) {
        if (result.method === 'fallback') {
          showSuccess(result.message);
        } else {
          showSuccess('Shared via WhatsApp successfully!');
        }
      } else {
        showError(result.error || 'Failed to share via WhatsApp');
      }
    } catch (error) {
      console.error('Error sharing via WhatsApp:', error);
      showError('Failed to share via WhatsApp. Please try again.');
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Handle cancel with confirmation
  const handleCancelClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };

  // Validation rules
  const validateField = (field, value) => {
    switch (field) {
      case 'BillNumber':
        if (!value || value.toString().trim() === '') return 'Bill Number is required';
        return '';
      case 'BillDate':
        if (!value) return 'Bill Date is required';
        return '';
      case 'MasterId':
        // Check for either MasterId or CustomerName
        if (!formData.MasterId && (!formData.CustomerName || formData.CustomerName.trim() === '')) {
          return 'Customer is required';
        }
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    const billNumberError = validateField('BillNumber', formData.BillNumber);
    if (billNumberError) newErrors.BillNumber = billNumberError;

    const billDateError = validateField('BillDate', formData.BillDate);
    if (billDateError) newErrors.BillDate = billDateError;

    // Customer validation - check for CustomerName instead of MasterId
    if (!formData.CustomerName && !formData.MasterId) {
      newErrors.MasterId = 'Customer is required';
    }

    // Sale details validation
    if (saleDetails.length === 0) {
      newErrors.saleDetails = 'At least one item is required';
    } else {
      const detailErrors = [];
      saleDetails.forEach((detail, index) => {
        const itemErrors = {};
        if (!detail.ItemId) itemErrors.ItemId = 'Item is required';
        if (!detail.Quantity || parseFloat(detail.Quantity) <= 0) {
          itemErrors.Quantity = 'Quantity must be greater than 0';
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.Rate = 'Rate must be greater than 0';
        }
        if (Object.keys(itemErrors).length > 0) {
          detailErrors[index] = itemErrors;
        }
      });
      if (detailErrors.length > 0) {
        newErrors.details = detailErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field blur for touched state
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));

    if (field === 'MasterId') {
      // Custom validation for customer field
      const error = (!formData.MasterId && (!formData.CustomerName || formData.CustomerName.trim() === ''))
        ? 'Customer is required'
        : '';
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      const error = validateField(field, formData[field]);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  // Handle save with validation
  const handleSaveWithValidation = () => {
    // Mark all fields as touched
    setTouched({ BillNumber: true, BillDate: true, MasterId: true });

    // Build validation error messages
    const validationMessages = [];

    if (!formData.BillNumber || formData.BillNumber.toString().trim() === '') {
      validationMessages.push('Bill Number is required');
    }
    if (!formData.BillDate) {
      validationMessages.push('Bill Date is required');
    }
    if (!formData.CustomerName && !formData.MasterId) {
      validationMessages.push('Customer is required');
    }
    if (saleDetails.length === 0) {
      validationMessages.push('At least one item is required');
    } else {
      // Check each item for Received Qty and Rate
      const itemErrors = [];
      saleDetails.forEach((detail, index) => {
        if (!detail.ItemId) {
          itemErrors.push(`Row ${index + 1}: Item is required`);
        }
        if (!detail.Quantity || parseFloat(detail.Quantity) <= 0) {
          itemErrors.push(`Row ${index + 1}: Received Qty is required`);
        }
        if (!detail.Rate || parseFloat(detail.Rate) <= 0) {
          itemErrors.push(`Row ${index + 1}: Rate is required`);
        }
        // Check balance stock for non-product items
        if (detail.ItemId && detail.Quantity) {
          const selectedItem = items.find(i => i.Id === detail.ItemId);
          const isProduct = selectedItem && (selectedItem.IsProduct === true || selectedItem.IsProduct === 1);
          if (!isProduct && selectedItem) {
            const qty = parseFloat(detail.Quantity) || 0;
            const prevQty = parseFloat(detail.PrevQuantity) || 0;
            const balanceStock = selectedItem.BalanceStock || 0;

            if (modalAction === 'edit') {
              // Only validate if qty increased beyond previous
              if (qty > prevQty) {
                const extraQtyNeeded = qty - prevQty;
                if (extraQtyNeeded > balanceStock) {
                  itemErrors.push(`Row ${index + 1}: ${selectedItem.Name} - Insufficient stock. Extra needed ${extraQtyNeeded.toFixed(2)}, Available ${balanceStock.toFixed(2)} (Short by ${(extraQtyNeeded - balanceStock).toFixed(2)})`);
                }
              }
            } else {
              if (qty > balanceStock) {
                const shortage = qty - balanceStock;
                itemErrors.push(`Row ${index + 1}: ${selectedItem.Name} - Insufficient stock. Requested ${qty}, Available ${balanceStock.toFixed(2)} (Short by ${shortage.toFixed(2)})`);
              }
            }
          }
        }
      });
      if (itemErrors.length > 0) {
        validationMessages.push(...itemErrors);
      }
    }

    // Validate related items stock balance
    if (relatedItems.length > 0) {
      const stockErrors = [];
      relatedItems.forEach((productGroup) => {
        // Find the parent product in saleDetails to get PrevQuantity
        const parentDetail = saleDetails.find(d => d.ItemId === productGroup.productId);
        const prevProductQty = parseFloat(parentDetail?.PrevQuantity) || 0;

        productGroup.items.forEach((relatedItem) => {
          const quantity = parseFloat(relatedItem.Quantity || relatedItem.Qty || 0);
          const originalQty = parseFloat(relatedItem.OriginalQuantity || 0);
          const componentItem = items.find(i => i.Id === (relatedItem.ItemId || relatedItem.ProductItemId));
          const balanceStock = componentItem?.BalanceStock || 0;

          if (modalAction === 'edit') {
            // Previous component qty that was already deducted from stock
            const prevComponentQty = originalQty * prevProductQty;
            // Only check if current qty is greater than previous
            if (quantity > prevComponentQty) {
              const extraQtyNeeded = quantity - prevComponentQty;
              if (extraQtyNeeded > balanceStock) {
                stockErrors.push(
                  `${productGroup.productName} - ${relatedItem.ProductChildName}: Extra needed ${extraQtyNeeded.toFixed(2)}, Available ${balanceStock.toFixed(2)} (Short by ${(extraQtyNeeded - balanceStock).toFixed(2)})`
                );
              }
            }
          } else {
            // Create mode: check full quantity
            if (balanceStock < quantity) {
              const shortage = quantity - balanceStock;
              stockErrors.push(
                `${productGroup.productName} - ${relatedItem.ProductChildName}: Required ${quantity.toFixed(2)}, Available ${balanceStock.toFixed(2)} (Short by ${shortage.toFixed(2)})`
              );
            }
          }
        });
      });
      
      if (stockErrors.length > 0) {
        validationMessages.push('Insufficient stock for product components:', ...stockErrors);
      }
    }

    // Validate payment amounts
    const totalAmount = parseFloat(formData.GrandTotal) || parseFloat(formData.TotalAmount) || 0;
    const cashAmount = parseFloat(formData.CashAmount) || 0;
    const upiAmount = parseFloat(formData.UPIAmount) || 0;
    const cardAmount = parseFloat(formData.CardAmount) || 0;
    const advanceAmount = parseFloat(formData.AdvanceAmount) || 0;
    const totalPayments = cashAmount + upiAmount + cardAmount + advanceAmount;

    if (totalPayments > totalAmount) {
      validationMessages.push(`Total payment amount (${formatCurrency(totalPayments)}) cannot exceed total amount (${formatCurrency(totalAmount)})`);
    }

    // Validate bank selection if UPI or Card amount is entered
    if ((upiAmount > 0 || cardAmount > 0) && !formData.BankId) {
      validationMessages.push('Bank selection is required when UPI or Card payment is made');
    }

    if (validationMessages.length > 0) {
      showError(validationMessages.join(' | '));
      validateForm();
      return;
    }

    if (validateForm()) {
      onSave();
    }
  };

  // Sync gstType with formData - REMOVED
  // useEffect(() => {
  //   if (formData.GstCalculationType) {
  //     setGstType(formData.GstCalculationType);
  //   }
  // }, [formData.GstCalculationType]);

  // const handleGstTypeChange = (value) => {
  //   const intValue = parseInt(value);
  //   setGstType(intValue);
  //   const gstTypeName = intValue === 2 ? 'Including Tax' : 'Excluding Tax';
  //   setFormData(prev => ({ ...prev, GstCalculationType: intValue, GstCalculationTypeName: gstTypeName }));
  // };

  const handleChange = (field, value) => {
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    if (field === 'CustomerName') {
      // Handle customer name change (from Autocomplete)
      const selectedCustomer = customers.find(s => s.Name === value);
      if (selectedCustomer) {
        // Existing customer selected
        const VATNumber = selectedCustomer.VATNumber || selectedCustomer.GSTNo || '';
        const masterId = selectedCustomer.MasterId || selectedCustomer.Id;

        setFormData(prev => ({
          ...prev,
          MasterId: masterId,
          CustomerName: value,
          VATNumber: VATNumber
        }));

        // Fetch customer closing balance
        fetchCustomerLedgerDetails(masterId);
        fetchCustomerAdvanceDetails(masterId);

        // Fetch bill number
        if (modalAction === 'create') {
          fetchBillNumber();
        }

        // Fetch and populate customer details for shipping section
        if (masterId) {
          fetchCustomerDetails(masterId).then(customerDetails => {
            setFormData(prev => {
              const creditDays = customerDetails.CreditDays ?? 0;
              const billDate = prev.BillDate || new Date().toISOString().split('T')[0];
              const dueDate = computeDueDate(billDate, creditDays);
              return {
                ...prev,
                ShipTo: customerDetails.customerName || value,
                ShipGSTNo: customerDetails.VATNumber,
                ShipToAddress1: customerDetails.addressLine1,
                ShipToAddress2: customerDetails.addressLine2,
                ShipToLocation: customerDetails.location,
                ShipToPincode: customerDetails.pincode,
                CreditDays: creditDays,
                DueDate: dueDate,
                // Always update currency when a customer is selected
                CurrencyTypeId: customerDetails.CurrencyTypeId || prev.CurrencyTypeId || null,
                CurrencyName: customerDetails.CurrencyName || prev.CurrencyName || ''
              };
            });
          });
        }
      } else {
        // New customer name typed
        setFormData(prev => ({
          ...prev,
          MasterId: null,
          CustomerName: value,
          VATNumber: '',
          StateId: null,
          CurrencyTypeId: null,
          CurrencyName: '',
          ShipTo: value,
          ShipGSTNo: '',
          ShipToAddress1: '',
          ShipToAddress2: '',
          ShipToLocation: '',
          ShipToPincode: '',
          CreditDays: 0,
          DueDate: prev.BillDate || new Date().toISOString().split('T')[0]
        }));

        // Clear closing balance for new customers
        setCustomerClosingBalance(null);
        setCustomerAdvanceAmount(null);

        // Fetch bill number for new customers
        if (modalAction === 'create') {
          fetchBillNumber();
        }
      }

      // Clear customer validation error when typing
      if (errors.MasterId && value && value.trim() !== '') {
        setErrors(prev => ({ ...prev, MasterId: '' }));
      }
    } else if (field === 'VATNumber') {
      // Handle VAT number change
      setFormData(prev => ({ ...prev, [field]: value }));
    } else if (field === 'BillDate') {
      // Recalculate DueDate when BillDate changes
      setFormData(prev => {
        const creditDays = parseInt(prev.CreditDays) || 0;
        const dueDate = computeDueDate(value, creditDays);
        return { ...prev, BillDate: value, DueDate: dueDate };
      });
    } else if (['CashAmount', 'UPIAmount', 'CardAmount', 'AdvanceAmount'].includes(field)) {
      // Handle payment amount changes
      const newValue = parseFloat(value) || 0;
      const totalAmount = parseFloat(formData.GrandTotal) || parseFloat(formData.TotalAmount) || 0;

      // Get current payment amounts
      const currentCash = field === 'CashAmount' ? newValue : (parseFloat(formData.CashAmount) || 0);
      const currentUPI = field === 'UPIAmount' ? newValue : (parseFloat(formData.UPIAmount) || 0);
      const currentCard = field === 'CardAmount' ? newValue : (parseFloat(formData.CardAmount) || 0);
      const currentAdvance = field === 'AdvanceAmount' ? newValue : (parseFloat(formData.AdvanceAmount) || 0);

      // Validate advance amount doesn't exceed customer advance
      if (field === 'AdvanceAmount' && newValue > (customerAdvanceAmount || 0)) {
        showError(`Advance amount cannot exceed customer advance balance (${formatCurrency(customerAdvanceAmount)})`);
        return;
      }

      // Calculate total payments
      const totalPayments = currentCash + currentUPI + currentCard + currentAdvance;

      // Validate total payments don't exceed total amount
      if (totalPayments > totalAmount) {
        showError(`Total payment amount (${formatCurrency(totalPayments)}) cannot exceed total amount (${formatCurrency(totalAmount)})`);
        return;
      }

      // Calculate balance
      const balanceAmount = totalAmount - totalPayments;
      const paidAmount = totalPayments;

      setFormData(prev => ({
        ...prev,
        [field]: newValue,
        PaidAmount: paidAmount,
        BalanceAmount: balanceAmount
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // VAT is always single tax (no split like CGST/SGST)
  const isIGSTInvoice = () => {
    return false; // VAT doesn't use IGST concept
  };

  const isIncludingTax = () => false; // Always excluding tax now

  const handleDetailChange = async (index, field, value) => {
    // Mark as having unsaved changes
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    const updatedDetails = [...saleDetails];

    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: '' };
        setSaleDetails(updatedDetails);
        calculateTotals(updatedDetails);
        return;
      }
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;
    }

    // If quantity is being changed, check stock balance
    if (field === 'Quantity' && updatedDetails[index].ItemId) {
      const selectedItem = items.find(i => i.Id === updatedDetails[index].ItemId);
      const isProduct = selectedItem && (selectedItem.IsProduct === true || selectedItem.IsProduct === 1);
      
      // Check balance stock for the item itself (non-product items)
      if (!isProduct && selectedItem) {
        const newQty = parseFloat(value) || 0;
        const prevQty = parseFloat(updatedDetails[index].PrevQuantity) || 0;
        const balanceStock = selectedItem.BalanceStock || 0;

        if (modalAction === 'edit') {
          // In edit mode: only check if new qty is greater than previous qty
          // The extra qty needed = newQty - prevQty, check if BalanceStock can cover it
          if (newQty > prevQty) {
            const extraQtyNeeded = newQty - prevQty;
            if (extraQtyNeeded > balanceStock) {
              showError(`Insufficient stock for ${selectedItem.Name}: Extra needed ${extraQtyNeeded.toFixed(2)}, Available ${balanceStock.toFixed(2)} (Short by ${(extraQtyNeeded - balanceStock).toFixed(2)})`);
              return;
            }
          }
        } else {
          // In create mode: check full quantity against BalanceStock
          if (newQty > balanceStock) {
            const shortage = newQty - balanceStock;
            showError(`Insufficient stock for ${selectedItem.Name}: Requested ${newQty}, Available ${balanceStock.toFixed(2)} (Short by ${shortage.toFixed(2)})`);
            return;
          }
        }
      }

      if (isProduct) {
        try {
          const response = await fetchData('SaleApi', `/ItemProductRelation/${updatedDetails[index].ItemId}/${companyId}`, token);
          const relatedItemsData = response.response_data || response || [];
          
          if (Array.isArray(relatedItemsData) && relatedItemsData.length > 0) {
            const productQuantity = parseFloat(value) || 0;
            const prevProductQty = parseFloat(updatedDetails[index].PrevQuantity) || 0;
            
            // Check stock balance for each related item
            const stockIssues = [];
            for (const relatedItem of relatedItemsData) {
              const originalQty = parseFloat(relatedItem.Quantity || relatedItem.Qty || 0);
              const requiredQty = originalQty * productQuantity;
              const componentItem = items.find(i => i.Id === (relatedItem.ProductItemId || relatedItem.ItemId));
              const availableStock = componentItem?.BalanceStock || 0;

              if (modalAction === 'edit') {
                // Previous component qty already deducted from stock
                const prevComponentQty = originalQty * prevProductQty;
                if (requiredQty > prevComponentQty) {
                  const extraQtyNeeded = requiredQty - prevComponentQty;
                  if (extraQtyNeeded > availableStock) {
                    stockIssues.push({
                      name: componentItem?.Name || 'Unknown Item',
                      required: extraQtyNeeded,
                      available: availableStock,
                      shortage: extraQtyNeeded - availableStock
                    });
                  }
                }
              } else {
                if (availableStock < requiredQty) {
                  stockIssues.push({
                    name: componentItem?.Name || 'Unknown Item',
                    required: requiredQty,
                    available: availableStock,
                    shortage: requiredQty - availableStock
                  });
                }
              }
            }
            
            // If there are stock issues, show error and prevent change
            if (stockIssues.length > 0) {
              const errorMessages = stockIssues.map(issue => 
                `${issue.name}: Required ${issue.required}, Available ${issue.available} (Short by ${issue.shortage})`
              ).join(' | ');
              showError(`Insufficient stock for product components: ${errorMessages}`);
              return;
            }
          }
        } catch (error) {
          console.error('Error checking product relation stock:', error);
          // Don't block the change if API fails, just log the error
        }
      }
    }

    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'VATRate'].includes(field)) {
      const detail = updatedDetails[index];
      const receivedQty = Math.max(0, parseFloat(detail.Quantity) || 0);
      // In create/edit mode, stock quantity equals received quantity (damage/excess/less are set during verification)
      const stockQty = receivedQty;
      const rate = Math.max(0, parseFloat(detail.Rate) || 0);
      const vatRate = Math.max(0, parseFloat(detail.VATRate) || 0);
      const useIGST = isIGSTInvoice();

      let taxableAmount, vatAmount, amount;

      if (isIncludingTax()) {
        // Rate includes VAT - calculate backwards
        const totalWithVat = receivedQty * rate;
        taxableAmount = totalWithVat / (1 + vatRate / 100);
        amount = taxableAmount;

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          discountPct = taxableAmount > 0 ? (discountAmount / taxableAmount) * 100 : 0;
        } else {
          discountPct = parseFloat(detail.DiscountPercentage) || 0;
          discountAmount = (taxableAmount * discountPct) / 100;
        }

        taxableAmount = taxableAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          StockQuantity: stockQty,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          Amount: amount,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: taxableAmount,
          VATAmount: vatAmount,
          CGSTAmount: 0,
          SGSTAmount: 0,
          IGSTAmount: 0,
          TotalAmount: taxableAmount + vatAmount
        };
      } else {
        // Rate excludes VAT - normal calculation
        amount = receivedQty * rate;

        let discountAmount = 0, discountPct = 0;
        if (field === 'DiscountAmount') {
          discountAmount = Math.max(0, parseFloat(value) || 0);
          discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
        } else {
          discountPct = parseFloat(detail.DiscountPercentage) || 0;
          discountAmount = (amount * discountPct) / 100;
        }

        taxableAmount = amount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          StockQuantity: stockQty,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          Amount: amount,
          DiscountPercentage: Math.round(discountPct * 100) / 100,
          DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: taxableAmount,
          VATAmount: vatAmount,
          CGSTAmount: 0,
          SGSTAmount: 0,
          IGSTAmount: 0,
          TotalAmount: taxableAmount + vatAmount
        };
      }
    }
    setSaleDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const handleItemSelect = async (index, itemId) => {
    // Check if item is already selected in another row
    const isDuplicate = saleDetails.some((detail, i) =>
      i !== index && detail.ItemId === parseInt(itemId)
    );

    if (isDuplicate) {
      showError('This item is already added to the table. Please select a different item.');
      return;
    }

    const selectedItem = items.find(i => i.Id === parseInt(itemId));
    if (selectedItem) {
      // Check if selected item is a product
      const isProduct = selectedItem.IsProduct === true || selectedItem.IsProduct === 1;
      
      // Check balance stock for non-product items
      if (!isProduct) {
        const currentDetail = saleDetails[index];
        const newQty = parseFloat(currentDetail.Quantity) || 1;
        const prevQty = parseFloat(currentDetail.PrevQuantity) || 0;
        const balanceStock = selectedItem.BalanceStock || 0;

        if (modalAction === 'edit') {
          if (newQty > prevQty) {
            const extraQtyNeeded = newQty - prevQty;
            if (extraQtyNeeded > balanceStock) {
              showError(`Insufficient stock for ${selectedItem.Name}: Extra needed ${extraQtyNeeded.toFixed(2)}, Available ${balanceStock.toFixed(2)} (Short by ${(extraQtyNeeded - balanceStock).toFixed(2)})`);
              return;
            }
          }
        } else {
          if (newQty > balanceStock) {
            const shortage = newQty - balanceStock;
            showError(`Insufficient stock for ${selectedItem.Name}: Requested ${newQty}, Available ${balanceStock.toFixed(2)} (Short by ${shortage.toFixed(2)})`);
            return;
          }
        }
      }

      // If it's a product, check stock balance for related items
      if (isProduct) {
        try {
          const response = await fetchData('SaleApi', `/ItemProductRelation/${itemId}/${companyId}`, token);
          const relatedItemsData = response.response_data || response || [];
          
          if (Array.isArray(relatedItemsData) && relatedItemsData.length > 0) {
            const updatedDetails = [...saleDetails];
            const productQuantity = parseFloat(updatedDetails[index].Quantity) || 1;
            
            // Check stock balance for each related item
            const stockIssues = [];
            for (const relatedItem of relatedItemsData) {
              const requiredQty = (parseFloat(relatedItem.Quantity || relatedItem.Qty || 0) * productQuantity);
              const componentItem = items.find(i => i.Id === (relatedItem.ProductItemId || relatedItem.ItemId));
              const availableStock = componentItem?.BalanceStock || 0;
              
              if (availableStock < requiredQty) {
                stockIssues.push({
                  name: componentItem?.Name || 'Unknown Item',
                  required: requiredQty,
                  available: availableStock,
                  shortage: requiredQty - availableStock
                });
              }
            }
            
            // If there are stock issues, show error and prevent selection
            if (stockIssues.length > 0) {
              const errorMessages = stockIssues.map(issue => 
                `${issue.name}: Required ${issue.required}, Available ${issue.available} (Short by ${issue.shortage})`
              ).join(' | ');
              showError(`Insufficient stock for product components: ${errorMessages}`);
              return;
            }
          }
        } catch (error) {
          console.error('Error checking product relation stock:', error);
          showError('Failed to validate product stock. Please try again.');
          return;
        }
      }
      
      const updatedDetails = [...saleDetails];
      const receivedQty = parseFloat(updatedDetails[index].Quantity) || 0;
      const rate = selectedItem.SaleRate || selectedItem.SaleRate || 0;
      const vatRate = selectedItem.VATRate || selectedItem.VATRate || 0; // Use VATRate, fallback to VATRate for compatibility
      const discountPct = parseFloat(updatedDetails[index].DiscountPercentage) || 0;
      const useIGST = isIGSTInvoice();

      let taxableAmount, vatAmount, amount;

      if (isIncludingTax()) {
        // Rate includes VAT
        const totalWithVat = receivedQty * rate;
        taxableAmount = totalWithVat / (1 + vatRate / 100);
        amount = taxableAmount;
        const discountAmount = (taxableAmount * discountPct) / 100;
        taxableAmount = taxableAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          ItemId: itemId,
          HSNCode: selectedItem.HSNCode || '',
          VATRate: vatRate,
          VATRate: vatRate, // Keep for backward compatibility
          Rate: rate,
          Amount: amount,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: receivedQty,
          DiscountAmount: discountAmount,
          TaxableAmount: taxableAmount,
          VATAmount: vatAmount,
          CGSTAmount: 0,
          SGSTAmount: 0,
          IGSTAmount: 0,
          TotalAmount: taxableAmount + vatAmount
        };
      } else {
        // Rate excludes VAT
        amount = receivedQty * rate;
        const discountAmount = (amount * discountPct) / 100;
        taxableAmount = amount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;

        updatedDetails[index] = {
          ...updatedDetails[index],
          ItemId: itemId,
          HSNCode: selectedItem.HSNCode || '',
          VATRate: vatRate,
          VATRate: vatRate, // Keep for backward compatibility
          Rate: rate,
          Amount: amount,
          BilledQuantity: receivedQty,
          OrderQuantity: receivedQty,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: receivedQty,
          DiscountAmount: discountAmount,
          TaxableAmount: taxableAmount,
          VATAmount: vatAmount,
          CGSTAmount: 0,
          SGSTAmount: 0,
          IGSTAmount: 0,
          TotalAmount: taxableAmount + vatAmount
        };
      }
      setSaleDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  };

  const addDetailRow = () => {
    setHasUnsavedChanges(true);
    setSaleDetails([{
      Id: 0, ItemId: '', ItemName: '', BatchNumber: '', SaleOrderId: null,
      OrderQuantity: 0, Quantity: 0, BilledQuantity: 0,
      DamageQuantity: 0, ExcessQuantity: 0, LessQuantity: 0, StockQuantity: 0,
      ReturnDamageQuantity: 0, ReturnStockQuantity: 0,
      Rate: 0, Amount: 0, DiscountPercentage: 0, DiscountAmount: 0,
      HSNCode: '', VATRate: 0, VATRate: 0, TaxableAmount: 0, VATAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0, IsActive: true
    }, ...saleDetails]);
  };

  const removeDetailRow = (index) => {
    setHasUnsavedChanges(true);
    const updatedDetails = saleDetails.filter((_, i) => i !== index);
    setSaleDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const calculateTotals = (details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      VATAmount: acc.VATAmount + (parseFloat(d.VATAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      DetailsTotalAmount: acc.DetailsTotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, VATAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, DetailsTotalAmount: 0 });

    const defaultFreightVATRate = parseFloat(freightPackingGST?.freight?.VATRate) || 0;
    const defaultPackingVATRate = parseFloat(freightPackingGST?.packing?.VATRate) || 0;
    const maxItemVATRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.VATRate) || 0)) : 0;
    const effectiveFreightVATRate = Math.max(defaultFreightVATRate, maxItemVATRate);
    const effectivePackingVATRate = Math.max(defaultPackingVATRate, maxItemVATRate);

    const freightAmount = parseFloat(formData.FreightAmount) || 0;
    const FreightVATAmount = (freightAmount * effectiveFreightVATRate) / 100;
    const packingAmount = parseFloat(formData.PackingAmount) || 0;
    const PackingVATAmount = (packingAmount * effectivePackingVATRate) / 100;
    const grandTotal = totals.DetailsTotalAmount + freightAmount + FreightVATAmount + packingAmount + PackingVATAmount;
    const roundOff = Math.round(grandTotal) - grandTotal;
    const totalAmount = Math.round(grandTotal);

    // Get current payment amounts
    const currentCash = parseFloat(formData.CashAmount) || 0;
    const currentUPI = parseFloat(formData.UPIAmount) || 0;
    const currentCard = parseFloat(formData.CardAmount) || 0;
    const currentAdvance = parseFloat(formData.AdvanceAmount) || 0;
    const currentTotalPayments = currentCash + currentUPI + currentCard + currentAdvance;

    // Auto-adjust cash amount when total changes
    let cashAmount = currentCash;
    let upiAmount = currentUPI;
    let cardAmount = currentCard;
    let advanceAmount = currentAdvance;

    // If total amount is 0 (no items), clear all payment fields
    if (totalAmount === 0) {
      cashAmount = 0;
      upiAmount = 0;
      cardAmount = 0;
      advanceAmount = 0;
    }
    // If customer has advance balance, apply advance first and keep cash at 0
    else if (customerAdvanceAmount > 0 && modalAction === 'create') {
      advanceAmount = Math.min(customerAdvanceAmount, totalAmount);
      cashAmount = 0;
      upiAmount = 0;
      cardAmount = 0;
    }
    // If no payments have been made yet, set cash amount to total amount
    else if (currentTotalPayments === 0 || (modalAction === 'create' && currentTotalPayments === 0)) {
      cashAmount = totalAmount;
      upiAmount = 0;
      cardAmount = 0;
      advanceAmount = 0;
    } else if (modalAction === 'create') {
      // In create mode, if only cash payment exists (no UPI/Card/Advance), auto-adjust cash to match total
      if (currentUPI === 0 && currentCard === 0 && currentAdvance === 0) {
        cashAmount = totalAmount;
      }
      // If there are other payments, keep them and adjust cash for the remainder
      else {
        const remainingAmount = totalAmount - currentUPI - currentCard - currentAdvance;
        cashAmount = Math.max(0, remainingAmount);
      }
    }

    const paidAmount = cashAmount + upiAmount + cardAmount + advanceAmount;
    const balanceAmount = totalAmount - paidAmount;

    setFormData(prev => ({
      ...prev,
      Amount: totals.Amount,
      DiscountAmount: totals.DiscountAmount,
      TaxableAmount: totals.TaxableAmount,
      VATAmount: totals.VATAmount,
      CGSTAmount: totals.CGSTAmount,
      SGSTAmount: totals.SGSTAmount,
      IGSTAmount: totals.IGSTAmount,
      FreightVATRate: effectiveFreightVATRate,
      FreightVATAmount: FreightVATAmount,
      PackingVATRate: effectivePackingVATRate,
      PackingVATAmount: PackingVATAmount,
      RoundOff: roundOff,
      TotalAmount: totals.Amount,
      GrandTotal: totalAmount,
      CashAmount: cashAmount,
      UPIAmount: upiAmount,
      CardAmount: cardAmount,
      AdvanceAmount: advanceAmount,
      BankId: (upiAmount === 0 && cardAmount === 0) ? null : prev.BankId,
      BalanceAmount: balanceAmount
    }));
  };

  useEffect(() => {
    // Only auto-calculate totals in create mode
    if (!isViewMode && modalAction === 'create') {
      calculateTotals(saleDetails);
    }
  }, [formData.FreightAmount, formData.PackingAmount]);

  // Recalculate totals in edit mode when freight/packing amounts change (user input)
  useEffect(() => {
    if (!isViewMode && modalAction === 'edit' && saleDetails.length > 0) {
      calculateTotals(saleDetails);
    }
  }, [formData.FreightAmount, formData.PackingAmount, saleDetails.length]);

  useEffect(() => {
    if (!isViewMode && saleDetails.length > 0 && formData.InvoiceTypeName) {
      const updatedDetails = saleDetails.map(d => {
        const vatRate = parseFloat(d.VATRate || d.VATRate) || 0;
        const taxableAmount = parseFloat(d.TaxableAmount) || 0;
        const vatAmount = (taxableAmount * vatRate) / 100;
        return { ...d, VATAmount: vatAmount, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0 };
      });
      setSaleDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  }, [formData.InvoiceTypeName]);

  const formatCurrency = (amt) => {
    // Dynamic currency symbol based on selected CurrencyName
    const getCurrencySymbol = (name) => {
      if (!name) return 'AED';
      const n = (typeof name === 'string' ? name : '').toLowerCase().trim();
      if (!n) return 'AED';
      const symbolMap = {
        'inr': '₹', 'rupee': '₹', 'indian rupee': '₹',
        'usd': '$', 'dollar': '$', 'us dollar': '$',
        'eur': '€', 'euro': '€',
        'gbp': '£', 'pound': '£', 'british pound': '£',
        'aed': 'د.إ', 'dirham': 'د.إ', 'uae dirham': 'د.إ',
        'sar': '﷼', 'riyal': '﷼', 'saudi riyal': '﷼',
        'jpy': '¥', 'yen': '¥',
        'cny': '¥', 'yuan': '¥', 'rmb': '¥',
        'aud': 'A$', 'australian dollar': 'A$',
        'cad': 'C$', 'canadian dollar': 'C$',
        'sgd': 'S$', 'singapore dollar': 'S$',
        'chf': 'CHF', 'swiss franc': 'CHF',
        'myr': 'RM', 'ringgit': 'RM',
        'thb': '฿', 'baht': '฿',
        'krw': '₩', 'won': '₩',
        'bdt': '৳', 'taka': '৳',
        'lkr': 'Rs', 'sri lankan rupee': 'Rs',
        'pkr': '₨', 'pakistani rupee': '₨',
        'npr': 'रू', 'nepalese rupee': 'रू',
        'zar': 'R', 'rand': 'R',
        'rub': '₽', 'ruble': '₽',
        'try': '₺', 'lira': '₺',
        'idr': 'Rp', 'rupiah': 'Rp',
        'php': '₱', 'peso': '₱',
        'vnd': '₫', 'dong': '₫',
        'ngn': '₦', 'naira': '₦',
        'egp': 'E£', 'egyptian pound': 'E£',
        'qar': 'QR', 'qatari riyal': 'QR',
        'kwd': 'KD', 'kuwaiti dinar': 'KD',
        'bhd': 'BD', 'bahraini dinar': 'BD',
        'omr': 'OMR', 'omani rial': 'OMR'
      };
      // Exact match first
      if (symbolMap[n]) return symbolMap[n];
      // Partial match - check if name contains any key
      for (const [key, symbol] of Object.entries(symbolMap)) {
        if (n.includes(key)) return symbol;
      }
      return name;
    };
    try {
      const symbol = getCurrencySymbol(formData.CurrencyName);
      return `${symbol} ${(parseFloat(amt) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch (e) {
      return ` ${(parseFloat(amt) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  const getSelectedCustomerGST = () => {
    if (formData.VATNumber) return formData.VATNumber;
    if (!formData.MasterId) return formData.VATNumber || '';
    const Customer = customers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return Customer?.VATNumber || Customer?.GSTNo || '';
  };

  const getCustomerDisplayName = () => {
    if (formData.CustomerName) return formData.CustomerName;
    if (formData.MasterName) return formData.MasterName;
    if (!formData.MasterId) return '';
    const Customer = customers.find(s => (s.MasterId || s.Id) === parseInt(formData.MasterId));
    return Customer?.Name || '';
  };

  const CustomerGSTNo = getSelectedCustomerGST();
  const CustomerDisplayName = getCustomerDisplayName();

  const disabledFieldStyle = { '& .MuiInputBase-input.Mui-disabled': { color: '#5D4037', fontWeight: 700, WebkitTextFillColor: '#5D4037' } };
  // Handle View All Items modal opening with minimal loading
  const handleViewAllItemsOpen = () => {
    setItemsModalOpen(true);
    // No loading for this simple modal
  };

  // Customer creation functions - Remove these
  const handleViewAllItemsClose = () => {
    setItemsModalOpen(false);
  };

  // Quick Add Client handlers
  const handleOpenClientModal = () => {
    setClientFormData({});
    setClientModalOpen(true);
  };

  const handleCloseClientModal = () => {
    setClientModalOpen(false);
    setClientFormData({});
  };

  const handleSaveClient = async () => {
    setClientLoading(true);
    try {
      const dataToSend = {
        Master: {
          Id: 0, CompanyId: companyId, BalanceSheetHeadId: clientFormData.BalanceSheetHeadId || 0,
          MasterTypeId: 1, Name: clientFormData.Name || '',
          SearchableName: clientFormData.SearchableName || clientFormData.Name || '',
          OpeningBalance: parseFloat(clientFormData.OpeningBalance) || 0,
          OpeningDate: clientFormData.OpeningDate ? clientFormData.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: clientFormData.IsPayableOrReceivable ?? true, IsActive: true, CreatedBy: userId
        },
        LedgerGroup: {
          Id: 0, CompanyId: companyId, MasterId: 0, Name: clientFormData.LedgerGroupName || '',
          CreditDays: parseInt(clientFormData.GroupCreditDays) || 0,
          CreditLimit: parseFloat(clientFormData.GroupCreditLimit) || 0, IsActive: true, CreatedBy: userId
        },
        Ledger: {
          Id: 0, CompanyId: companyId, MasterId: 0, LedgerGroupId: 0, IsType: 1,
          InvoiceTypeId: clientFormData.InvoiceTypeId || 0, SalesExecutiveId: clientFormData.SalesExecutiveId || 0,
          StateCodeId: clientFormData.StateCodeId || null,
          AddressLine1: clientFormData.AddressLine1 || '', AddressLine2: clientFormData.AddressLine2 || '',
          Location: clientFormData.Location || '', PinCode: clientFormData.PinCode || '',
          CountryId: clientFormData.CountryId || null, ISOCode: clientFormData.ISOCode || '',
          ContactNumber1: clientFormData.ContactNumber1 || '', ContactNumber2: clientFormData.ContactNumber2 || '',
          ContactNumber3: clientFormData.ContactNumber3 || '', Email: clientFormData.Email || '',
          VATNumber: clientFormData.VATNumber || '', PanNumber: clientFormData.PanNumber || '',
          MSMENumber: clientFormData.MSMENumber || '',
          CreditDays: parseInt(clientFormData.CreditDays) || 0, CreditLimit: parseFloat(clientFormData.CreditLimit) || 0,
          IECCode: clientFormData.IECCode || '', TradeLicenseNo: clientFormData.TradeLicenseNo || '',
          CurrencyTypeId: clientFormData.CurrencyTypeId || null, CurrencyName: clientFormData.CurrencyName || '',
          OpeningBalance: parseFloat(clientFormData.OpeningBalance) || 0,
          OpeningDate: clientFormData.OpeningDate ? clientFormData.OpeningDate.split('T')[0] : new Date().toISOString().split('T')[0],
          IsPayableOrReceivable: true, IsActive: true, CreatedBy: userId
        }
      };

      await postData('ClientAndVendorApi', '', dataToSend, token);

      // Refresh customers list and auto-select
      const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
      const updatedCustomers = response.response_data || response || [];
      customers.length = 0;
      updatedCustomers.forEach(c => customers.push(c));

      const newCustomer = updatedCustomers.find(c => c.Name === clientFormData.Name);
      if (newCustomer) {
        handleChange('CustomerName', newCustomer.Name);
      }
      handleCloseClientModal();
    } catch (error) {
      console.error('Error saving client:', error);
    }
    setClientLoading(false);
  };

  // Quick Add Item handlers
  const handleOpenQuickItemModal = async () => {
    try {
      const [catRes, brandRes, uomRes] = await Promise.allSettled([
        fetchData('CategoryApi', `/${companyId}`, token),
        fetchData('BrandApi', `/${companyId}`, token),
        fetchData('UOMApi', `/${companyId}`, token)
      ]);
      setQuickItemCategories(catRes.status === 'fulfilled' ? (catRes.value.response_data || catRes.value || []) : []);
      setQuickItemBrands(brandRes.status === 'fulfilled' ? (brandRes.value.response_data || brandRes.value || []) : []);
      setQuickItemUoms(uomRes.status === 'fulfilled' ? (uomRes.value.response_data || uomRes.value || []) : []);
    } catch (e) {
      console.error('Error fetching item master data:', e);
    }
    setQuickItemModalOpen(true);
  };

  const handleCloseQuickItemModal = () => {
    setQuickItemModalOpen(false);
  };

  const handleQuickItemSaved = () => {
    setQuickItemModalOpen(false);
  };

  // Handle Get Items modal
  const handleOpenGetItemsModal = async () => {
    setGetItemsModalOpen(true);
    setGetItemsLoading(true);
    setSelectedItems([]);
    setGetItemsSearchValue('');
    setCurrentItemsPage(1);

    const loadingTimeout = setTimeout(() => {
      console.warn('Get Items loading timeout - stopping loading');
      setGetItemsLoading(false);
    }, 3000);

    try {
      // Items are already loaded in the parent component
      console.log('Items already available:', items.length);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      clearTimeout(loadingTimeout);
      setGetItemsLoading(false);
    }
  };

  // Handle Ledger Modal
  const handleOpenGetLedgerModal = () => {
    if (!formData.MasterId) {
      showError('Please select a customer first');
      return;
    }

    // Helper function to format date without timezone issues
    const formatDateLocal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Set default date range (current month)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const fromDateStr = formatDateLocal(firstDay);
    const toDateStr = formatDateLocal(lastDay);

    setLedgerFromDate(fromDateStr);
    setLedgerToDate(toDateStr);
    setLedgerModalOpen(true);

    // Fetch initial ledger data
    fetchLedgerData(formData.MasterId, fromDateStr, toDateStr);
  };

  const handleCloseLedgerModal = () => {
    setLedgerModalOpen(false);
    setLedgerData([]);
    setLedgerFromDate('');
    setLedgerToDate('');
  };

  // Fetch ledger data
  const fetchLedgerData = async (masterId, fromDate, toDate) => {
    if (!masterId || !fromDate || !toDate) return;

    setLedgerLoading(true);
    try {
      const response = await fetchData('SaleApi', `/LedgerDetails/${companyId}/${masterId}/${masterId}/${fromDate}/${toDate}`, token);
      setLedgerData(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      setLedgerData([]);
      showError('Failed to fetch ledger data');
    } finally {
      setLedgerLoading(false);
    }
  };

  // Handle ledger date change
  const handleLedgerDateChange = () => {
    if (ledgerFromDate && ledgerToDate && formData.MasterId) {
      fetchLedgerData(formData.MasterId, ledgerFromDate, ledgerToDate);
    }
  };

  // Auto-refresh ledger when customer changes (if modal is open)
  useEffect(() => {
    if (ledgerModalOpen && formData.MasterId && ledgerFromDate && ledgerToDate) {
      fetchLedgerData(formData.MasterId, ledgerFromDate, ledgerToDate);
    } else if (ledgerModalOpen && !formData.MasterId) {
      // Close ledger modal if customer is cleared
      handleCloseLedgerModal();
    }
  }, [formData.MasterId]);

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearchValue('');
  };

  // Handle item selection in Get Items modal
  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      const newSelectedItem = {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.SaleRate || 0,
        selectedDiscountPercentage: 0,
        selectedDiscountAmount: 0
      };
      setSelectedItems(prev => [...prev, newSelectedItem]);
    } else {
      setSelectedItems(prev => prev.filter(selectedItem => selectedItem.Id !== item.Id));
    }
  };

  // Fetch related items for products in sale details
  const fetchRelatedItemsForSale = async () => {
    console.log('=== fetchRelatedItemsForSale START ===');
    console.log('saleDetails:', saleDetails);
    console.log('items:', items);
    console.log('Sample item with BalanceStock:', items.find(i => i.BalanceStock > 0));
    console.log('Items array length:', items.length);
    console.log('First 3 items:', items.slice(0, 3));

    // Find all products in sale details
    const productItems = saleDetails.filter(detail => {
      const item = items.find(i => i.Id === detail.ItemId);
      const isProduct = item && (item.IsProduct === true || item.IsProduct === 1);
      console.log('Checking item:', {
        itemId: detail.ItemId,
        itemName: item?.Name,
        isProduct,
        IsProduct: item?.IsProduct,
        item: item
      });
      return isProduct;
    });

    console.log('Product items found:', productItems.length);
    console.log('Product items details:', productItems);

    if (productItems.length === 0) {
      console.log('No products found, clearing related items');
      setRelatedItems([]);
      return;
    }

    setRelatedItemsLoading(true);
    try {
      // Fetch related items for all products
      const promises = productItems.map(detail => {
        const item = items.find(i => i.Id === detail.ItemId);
        console.log('Fetching related items for product:', {
          itemId: detail.ItemId,
          itemName: item?.Name,
          quantity: detail.Quantity
        });
        return fetchData('SaleApi', `/ItemProductRelation/${detail.ItemId}/${companyId}`, token)
          .then(response => {
            console.log('Related items response for product', detail.ItemId, ':', response);
            const relatedItemsData = response.response_data || response || [];
            const productQuantity = parseFloat(detail.Quantity) || 1;
            
            console.log('Raw related items data:', relatedItemsData);
            if (relatedItemsData.length > 0) {
              console.log('First related item structure:', relatedItemsData[0]);
              console.log('First related item keys:', Object.keys(relatedItemsData[0]));
            }

            // Multiply related item quantities by product quantity
            const adjustedItems = Array.isArray(relatedItemsData)
              ? relatedItemsData.map(relatedItem => {
                  // The correct property is ProductItemId (as shown in console)
                  const itemId = relatedItem.ProductItemId || relatedItem.ItemId || relatedItem.ProductChildId || relatedItem.ChildItemId || relatedItem.Id;
                  const itemName = relatedItem.ProductChildName || relatedItem.ItemName || relatedItem.Name;
                  
                  // Find the component item in the items array by ID first, then by name
                  let componentItem = items.find(i => i.Id === itemId);
                  if (!componentItem && itemName) {
                    componentItem = items.find(i => i.Name === itemName);
                  }
                  
                  console.log('Looking up component:', {
                    relatedItemKeys: Object.keys(relatedItem),
                    itemId: itemId,
                    itemName: itemName,
                    foundComponent: !!componentItem,
                    componentId: componentItem?.Id,
                    balanceStock: componentItem?.BalanceStock,
                    totalItemsInArray: items.length
                  });

                  const originalQty = parseFloat(relatedItem.Quantity || relatedItem.Qty || 0);
                  const currentQty = originalQty * productQuantity;
                  // In edit mode, PrevQuantity = OriginalQuantity × PrevProductQty (what was previously saved)
                  const prevProductQty = parseFloat(detail.PrevQuantity) || 0;
                  const prevComponentQty = modalAction === 'edit' ? (originalQty * prevProductQty) : 0;
                  
                  return {
                    ...relatedItem,
                    ItemId: componentItem?.Id || itemId, // Use found component ID or fallback
                    OriginalQuantity: originalQty,
                    Quantity: currentQty,
                    PrevQuantity: prevComponentQty,
                    ProductQuantity: productQuantity,
                    BalanceStock: componentItem?.BalanceStock || 0, // Add balance stock from items array
                    Rate: relatedItem.Rate || componentItem?.SaleRate || 0, // Ensure rate is available
                    SaleRate: relatedItem.SaleRate || componentItem?.SaleRate || 0
                  };
                })
              : [];

            console.log('Adjusted items for product', detail.ItemId, ':', adjustedItems);

            return {
              productId: detail.ItemId,
              productName: items.find(i => i.Id === detail.ItemId)?.Name || '',
              productQuantity: productQuantity,
              items: adjustedItems
            };
          })
          .catch(error => {
            console.error(`Error fetching related items for product ${detail.ItemId}:`, error);
            return {
              productId: detail.ItemId,
              productName: items.find(i => i.Id === detail.ItemId)?.Name || '',
              productQuantity: 0,
              items: []
            };
          });
      });

      const results = await Promise.all(promises);
      console.log('All results:', results);

      // Filter out products with no related items
      const filteredResults = results.filter(r => r.items.length > 0);
      console.log('Filtered results:', filteredResults);
      console.log('Number of products with components:', filteredResults.length);

      setRelatedItems(filteredResults);
      console.log('=== fetchRelatedItemsForSale END ===');
    } catch (error) {
      console.error('Error fetching related items:', error);
      setRelatedItems([]);
    } finally {
      setRelatedItemsLoading(false);
    }
  };

  // Fetch related items when sale details change
  // In edit/view mode with API data already loaded, skip re-fetching
  useEffect(() => {
    if (open && saleDetails.length > 0 && items.length > 0) {
      // In edit/view mode, if relatedItemsProp has data from API, use it and don't re-fetch
      if ((modalAction === 'edit' || modalAction === 'view') && relatedItemsProp && relatedItemsProp.length > 0 && !initialRelatedItemsLoaded) {
        console.log('useEffect - using relatedItems from API prop data');
        setRelatedItems(relatedItemsProp);
        setInitialRelatedItemsLoaded(true);
        return;
      }
      // If already loaded from API, don't re-fetch
      if (initialRelatedItemsLoaded) {
        return;
      }
      // Create mode or no API data: fetch from ItemProductRelation
      console.log('useEffect triggered - fetching related items');
      fetchRelatedItemsForSale();
    } else {
      if (!initialRelatedItemsLoaded) {
        setRelatedItems([]);
      }
    }
  }, [saleDetails, open, items]);

  // Handle related item quantity change
  const handleRelatedItemQtyChange = (groupIndex, itemIndex, newQty) => {
    if (!isViewMode) {
      setHasUnsavedChanges(true);
    }

    const updatedRelatedItems = [...relatedItems];
    const value = parseFloat(newQty) || 0;

    if (value < 0) return;

    // Get the related item and check stock balance
    const relatedItem = updatedRelatedItems[groupIndex].items[itemIndex];
    const componentItem = items.find(i => i.Id === (relatedItem.ItemId || relatedItem.ProductItemId));
    const balanceStock = componentItem?.BalanceStock || 0;

    // Show warning if quantity exceeds available stock
    if (modalAction === 'edit') {
      // In edit mode: find previous component qty from PrevQuantity of parent product
      const productGroup = updatedRelatedItems[groupIndex];
      const parentDetail = saleDetails.find(d => d.ItemId === productGroup.productId);
      const prevProductQty = parseFloat(parentDetail?.PrevQuantity) || 0;
      const originalQty = parseFloat(relatedItem.OriginalQuantity) || 0;
      const prevComponentQty = originalQty * prevProductQty;

      if (value > prevComponentQty) {
        const extraQtyNeeded = value - prevComponentQty;
        if (extraQtyNeeded > balanceStock) {
          showError(`Warning: ${relatedItem.ProductChildName} - Extra needed ${extraQtyNeeded.toFixed(2)}, Available ${balanceStock.toFixed(2)} (Short by ${(extraQtyNeeded - balanceStock).toFixed(2)})`);
        }
      }
    } else {
      if (value > balanceStock) {
        const shortage = value - balanceStock;
        showError(`Warning: ${relatedItem.ProductChildName} - Requested ${value.toFixed(2)}, Available ${balanceStock.toFixed(2)} (Short by ${shortage.toFixed(2)})`);
      }
    }

    updatedRelatedItems[groupIndex].items[itemIndex] = {
      ...updatedRelatedItems[groupIndex].items[itemIndex],
      Quantity: value,
      Qty: value
    };

    setRelatedItems(updatedRelatedItems);
  };

  // Update selected item details
  const handleSelectedItemChange = (itemId, field, value) => {
    setSelectedItems(prev => prev.map(item =>
      item.Id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Handle search in Get Items modal
  const handleGetItemsSearch = (searchValue) => {
    setGetItemsSearchValue(searchValue);
    setCurrentItemsPage(1);
  };

  // Filter items based on search value for Get Items modal
  const getFilteredItems = () => {
    if (!getItemsSearchValue.trim()) {
      return items;
    }

    const searchTerm = getItemsSearchValue.toLowerCase().trim();
    return items.filter(item =>
      item.Name?.toLowerCase().includes(searchTerm) ||
      item.HSNCode?.toLowerCase().includes(searchTerm) ||
      item.Code?.toLowerCase().includes(searchTerm)
    );
  };

  // Get paginated items for Get Items modal
  const getPaginatedItems = () => {
    const filtered = getFilteredItems();
    const startIndex = (currentItemsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = Math.ceil(getFilteredItems().length / itemsPerPage);

  // Check if item is already in sale details
  const isItemAlreadyInSale = (itemId) => {
    return saleDetails.some(detail => detail.ItemId === parseInt(itemId));
  };

  // Add selected items to sale details
  const handleAddSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    setAddingItemsLoading(true);

    try {
      const useIGST = isIGSTInvoice();

      const itemsToAdd = selectedItems.filter(
        selectedItem => !isItemAlreadyInSale(selectedItem.Id)
      );

      if (itemsToAdd.length === 0) {
        handleCloseGetItemsModal();
        return;
      }

      const newItems = itemsToAdd.map(selectedItem => {
        const quantity = parseFloat(selectedItem.selectedQuantity) || 0;
        const rate = parseFloat(selectedItem.selectedRate) || 0;
        const vatRate = parseFloat(selectedItem.VATRate || selectedItem.VATRate) || 0;
        const discountPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
        const discountAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
        const amount = quantity * rate;

        const finalDiscountAmount = discountAmt > 0 ? discountAmt : (amount * discountPct) / 100;
        const taxableAmount = amount - finalDiscountAmount;
        const vatAmount = (taxableAmount * vatRate) / 100;

        return {
          Id: 0,
          ItemId: selectedItem.Id,
          HSNCode: selectedItem.HSNCode || '',
          VATRate: vatRate,
          VATRate: vatRate, // Keep for backward compatibility
          Quantity: quantity,
          Rate: rate,
          Amount: amount,
          DiscountPercentage: discountAmt > 0 ? (finalDiscountAmount / amount) * 100 : discountPct,
          DiscountAmount: finalDiscountAmount,
          TaxableAmount: taxableAmount,
          VATAmount: vatAmount,
          CGSTAmount: 0,
          SGSTAmount: 0,
          IGSTAmount: 0,
          TotalAmount: taxableAmount + vatAmount,
          BilledQuantity: quantity,
          OrderQuantity: quantity,
          DamageQuantity: 0,
          ExcessQuantity: 0,
          LessQuantity: 0,
          StockQuantity: quantity,
          IsActive: true
        };
      });

      const updatedDetails = [...saleDetails, ...newItems];
      setSaleDetails(updatedDetails);

      handleCloseGetItemsModal();

      // Calculate totals
      setTimeout(() => {
        calculateTotals(updatedDetails);
      }, 10);

    } catch (error) {
      console.error('Error adding selected items:', error);
      showError('Failed to add selected items. Please try again.');
    } finally {
      setAddingItemsLoading(false);
    }
  };

  // Filter invoice types for new customers
  const compactInput = {
    '& .MuiInputBase-input': { py: 0.4, fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    '& .MuiInputBase-root': { minHeight: '28px' },
    '& .MuiOutlinedInput-root': { minHeight: '28px' },
    '& .MuiSelect-select': { py: 0.4 },
    ...(isViewMode && disabledFieldStyle)
  };

  return (
    <>
      <Dialog open={open} onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          if (hasUnsavedChanges && !isViewMode) {
            setCancelConfirmOpen(true);
            return;
          }
        }
        onClose();
      }}
        maxWidth="xl" fullWidth fullScreen={isMobile} disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, maxWidth: '1400px', position: 'relative' } }}>

        <DialogTitle sx={{ py: 1, px: 2, borderBottom: isViewMode ? '1px solid #e2e8f0' : '1px solid #ddd6fe', bgcolor: isViewMode ? '#1e293b' : '#f5f3ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: isViewMode ? '#f8fafc' : '#7c3aed', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: isViewMode ? '#f8fafc' : '#5b21b6', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
            </Box>
            <IconButton onClick={handleCancelClick} size="small" sx={{ p: 0.5, color: isViewMode ? '#f8fafc' : 'inherit' }}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1 : 1.5, bgcolor: isViewMode ? '#f8fafc' : '#fefffe' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#7c3aed' }}>Sale Information</Typography>

            {/* Customer Closing Balance in header */}
            {formData.MasterId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{
                  fontSize: '0.75rem',
                  color: '#374151',
                  fontWeight: 600
                }}>
                  Ledger Bal:
                </Typography>
                {loadingBalance ? (
                  <CircularProgress size={12} />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: customerClosingBalance >= 0 ? '#7c3aed' : '#dc2626',
                      bgcolor: customerClosingBalance >= 0 ? '#f5f3ff' : '#fef2f2',
                      px: 1,
                      py: 0.4,
                      borderRadius: 1,
                      border: `1px solid ${customerClosingBalance >= 0 ? '#bbf7d0' : '#fecaca'}`,
                      minWidth: '80px',
                      textAlign: 'center',
                      display: 'inline-block'
                    }}>
                      {formatCurrency(Math.abs(customerClosingBalance || 0))}
                      {customerClosingBalance < 0 ? ' (Dr)' : ' (Cr)'}
                    </Typography>
                    <Button
                      size="small"
                      onClick={handleOpenGetLedgerModal}
                      sx={{
                        minWidth: 'auto',
                        px: 0.75,
                        py: 0.25,
                        fontSize: '0.65rem',
                        bgcolor: '#1e40af',
                        color: '#fff',
                        '&:hover': { bgcolor: '#1d4ed8' }
                      }}
                    >
                      Ledger
                    </Button>
                  </Box>
                )}

                {/* Customer Advance Amount */}
                {loadingAdvance ? (
                  <CircularProgress size={12} />
                ) : (
                  customerAdvanceAmount > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                      <Typography variant="caption" sx={{
                        fontSize: '0.75rem',
                        color: '#374151',
                        fontWeight: 600
                      }}>
                        Advance:
                      </Typography>
                      <Typography variant="caption" sx={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        color: '#059669',
                        bgcolor: '#ecfdf5',
                        px: 1,
                        py: 0.4,
                        borderRadius: 1,
                        border: '1px solid #a7f3d0',
                        minWidth: '80px',
                        textAlign: 'center',
                        display: 'inline-block'
                      }}>
                        {formatCurrency(Math.abs(customerAdvanceAmount))}
                      </Typography>
                    </Box>
                  )
                )}
              </Box>
            )}
          </Box>

          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={6} sm={1.2}>
              <TextField
                fullWidth
                size="small"
                label="Bill No *"
                value={formData.BillPrefix && formData.FinYear
                  ? `${formData.BillPrefix}/${formData.BillNumber}/${formData.FinYear}`
                  : formData.BillNumber || ''}
                disabled={true}
                error={touched.BillNumber && !!errors.BillNumber}
                helperText={touched.BillNumber && errors.BillNumber}
                sx={{
                  ...compactInput,
                  '& .MuiInputBase-input.Mui-disabled': {
                    color: '#7c3aed',
                    fontWeight: 700,
                    WebkitTextFillColor: '#7c3aed'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ShoppingCart sx={{ fontSize: 16, color: '#7c3aed' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} sm={1.3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Bill Date *"
                value={formData.BillDate || ''}
                onChange={(e) => handleChange('BillDate', e.target.value)}
                onBlur={() => handleBlur('BillDate')}
                disabled={isViewMode}
                error={touched.BillDate && !!errors.BillDate}
                helperText={touched.BillDate && errors.BillDate}
                InputLabelProps={{ shrink: true }}
                sx={compactInput}
              />
            </Grid>
            <Grid item xs={6} sm={1.3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Due Date"
                value={formData.DueDate || ''}
                onChange={(e) => handleChange('DueDate', e.target.value)}
                disabled={isViewMode}
                InputLabelProps={{ shrink: true }}
                sx={compactInput}
              />
            </Grid>
            <Grid item xs={6} sm={3.5}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="Customer" value={CustomerDisplayName || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
              ) : (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                  <Autocomplete
                    freeSolo
                    options={customers.map(customer => customer.Name)}
                    value={formData.CustomerName || CustomerDisplayName || ''}
                    onChange={(event, newValue) => {
                      handleChange('CustomerName', newValue || '');
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      setCustomerInputValue(newInputValue);
                      // Only trigger handleChange on actual user input, not on programmatic reset
                      if (reason === 'input') {
                        handleChange('CustomerName', newInputValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Customer *"
                        error={touched.MasterId && !!errors.MasterId}
                        helperText={touched.MasterId && errors.MasterId}
                        onBlur={() => {
                          // Only validate if the field is actually empty
                          const currentValue = formData.CustomerName || '';
                          if (currentValue.trim() === '') {
                            handleBlur('MasterId');
                          } else {
                            // Clear error if there's a value
                            setErrors(prev => ({ ...prev, MasterId: '' }));
                          }
                        }}
                        sx={compactInput}
                      />
                    )}
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-root': { minHeight: '28px', py: '0 !important' },
                      '& .MuiInputBase-input': { py: '0.4 !important', fontSize: '0.8rem' },
                      '& .MuiAutocomplete-input': { py: '3.5px !important' },
                      '& .MuiOutlinedInput-root': { minHeight: '28px', padding: '0 9px !important' },
                      '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' },
                      '& .MuiAutocomplete-clearIndicator': { padding: '2px', fontSize: '0.9rem' },
                      '& .MuiAutocomplete-popupIndicator': { padding: '2px' }
                    }}
                    ListboxProps={{
                      style: { maxHeight: 150, fontSize: '0.85rem' }
                    }}
                  />
                  <Tooltip title="Add New Customer">
                    <IconButton
                      size="small"
                      onClick={handleOpenClientModal}
                      sx={{ mt: 0.3, bgcolor: '#1e3a5f', color: '#fff', borderRadius: '6px', width: 28, height: 28, flexShrink: 0,
                        '&:hover': { bgcolor: '#2d5a8e' } }}
                    >
                      <PersonAdd sx={{ fontSize: '0.85rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Grid>

            <Grid item xs={6} sm={1.7}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="VAT No" value={CustomerGSTNo || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: CustomerGSTNo ? '#ddd6fe' : '#f9fafb' } }} />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="VAT No"
                  value={CustomerGSTNo || ''}
                  onChange={(e) => handleChange('VATNumber', e.target.value)}
                  disabled={formData.MasterId ? true : false} // Editable for new customers, disabled for existing
                  sx={{
                    ...compactInput,
                    ...(formData.MasterId && disabledFieldStyle),
                    '& .MuiInputBase-root': {
                      bgcolor: CustomerGSTNo ? '#ddd6fe' : '#f9fafb'
                    }
                  }}
                />
              )}
            </Grid>
            <Grid item xs={6} sm={1.5}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="Currency" value={formData.CurrencyName || 'AED'} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
              ) : (
                <Autocomplete
                  options={Array.isArray(currencies) ? currencies : []}
                  getOptionLabel={(option) => option.Currency || ''}
                  isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                  value={Array.isArray(currencies) && formData.CurrencyTypeId ? currencies.find(c => c.Id === parseInt(formData.CurrencyTypeId)) || null : null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFormData(prev => ({
                        ...prev,
                        CurrencyTypeId: newValue.Id,
                        CurrencyName: newValue.Currency || ''
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        CurrencyTypeId: null,
                        CurrencyName: ''
                      }));
                    }
                    if (!isViewMode) setHasUnsavedChanges(true);
                  }}
                  disabled={currencyLoading}
                  loading={currencyLoading}
                  ListboxProps={{
                    style: { fontSize: '0.8rem', maxHeight: '200px' }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Currency"
                      size="small"
                      placeholder="Select"
                      sx={compactInput}
                      InputProps={{
                        ...params.InputProps,
                        style: { fontSize: '0.8rem' },
                        endAdornment: (
                          <>
                            {currencyLoading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiInputBase-root': { minHeight: '28px', py: '0 !important' },
                    '& .MuiInputBase-input': { py: '0.4 !important', fontSize: '0.8rem' },
                    '& .MuiAutocomplete-input': { py: '3.5px !important' },
                    '& .MuiOutlinedInput-root': { minHeight: '28px', padding: '0 9px !important' },
                    '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' },
                    '& .MuiAutocomplete-clearIndicator': { padding: '2px', fontSize: '0.9rem' },
                    '& .MuiAutocomplete-popupIndicator': { padding: '2px' }
                  }}
                />
              )}
            </Grid>
            <Grid item xs={6} sm={1.5}>
              {isViewMode ? (
                <TextField fullWidth size="small" label="Salesman" value={getSalesmanName(formData.SalesManId) || '-'} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
              ) : (
                <FormControl fullWidth size="small" sx={compactInput}>
                  <InputLabel>Salesman</InputLabel>
                  <Select
                    value={formData.SalesManId || ''}
                    onChange={(e) => handleChange('SalesManId', e.target.value)}
                    label="Salesman"
                    sx={{ fontSize: '0.8rem' }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}
                  >
                    {salesmen.map(salesman => (
                      <MenuItem key={salesman.MasterId || salesman.Id} value={salesman.MasterId || salesman.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                        {salesman.MasterName || salesman.Name || `${salesman.FirstName || ''} ${salesman.LastName || ''}`.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 0.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: errors.saleDetails ? '#dc2626' : (isViewMode ? '#475569' : '#7c3aed') }}>Sale Items *</Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>({saleDetails.length} items)</Typography>
              {errors.saleDetails && (
                <Typography variant="caption" sx={{ color: '#dc2626', fontSize: '0.7rem' }}>({errors.saleDetails})</Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {saleDetails.length > 5 && (
                <Button
                  size="small"
                  onClick={handleViewAllItemsOpen}
                  startIcon={<Visibility sx={{ fontSize: 14 }} />}
                  sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#1e40af' }}
                >
                  View All
                </Button>
              )}

              {!isViewMode && (
                <>
                  <Button
                    size="small"
                    startIcon={<List sx={{ fontSize: 14 }} />}
                    onClick={handleOpenGetItemsModal}
                    variant="text"
                    sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#7c3aed' }}
                  >
                    Get Items
                  </Button>
                  {/* <Button
                    size="small"
                    startIcon={<AddBox sx={{ fontSize: 14 }} />}
                    onClick={handleOpenQuickItemModal}
                    variant="text"
                    sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0, color: '#1e3a5f' }}
                  >
                    New Item
                  </Button> */}
                </>
              )}
            </Box>
          </Box>

          {/* Desktop Table View - Fixed height for 5 rows */}
          {!isMobile && (
            <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 1, border: errors.saleDetails ? '1px solid #dc2626' : (isViewMode ? '1px solid #e2e8f0' : '1px solid #ddd6fe'), borderRadius: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: isViewMode ? 'linear-gradient(135deg, #334155 0%, #1e293b 100%)' : 'linear-gradient(135deg, #4c0434ff 0%, #6d28d9 100%)' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #ddd6fe', width: '4%', background: 'transparent', color: 'white', fontWeight: 600 }}>Sl No</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #ddd6fe', width: '20%', background: 'transparent', color: 'white', fontWeight: 600 }}>Item</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #ddd6fe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>Qty</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #ddd6fe', width: '12%', background: 'transparent', color: 'white', fontWeight: 600 }}>Rate</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #ddd6fe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>Disc%</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #ddd6fe', width: '12%', background: 'transparent', color: 'white', fontWeight: 600 }}>Disc Amt</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #ddd6fe', width: '8%', background: 'transparent', color: 'white', fontWeight: 600 }}>VAT%</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #ddd6fe', width: '14%', background: 'transparent', color: 'white', fontWeight: 600 }}>Taxable</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #ddd6fe', width: '14%', background: 'transparent', color: 'white', fontWeight: 600 }}>Total</th>
                    {!isViewMode && <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #ddd6fe', width: '4%', background: 'transparent', color: 'white', fontWeight: 600 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {saleDetails.map((d, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? (isViewMode ? '#f1f5f9' : '#f5f3ff') : '#ffffff' }}>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd6fe', textAlign: 'center', fontWeight: 600, color: isViewMode ? '#475569' : '#7c3aed' }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd6fe', fontWeight: 600 }}>
                        <span style={{ fontWeight: 600, color: '#5b21b6' }}>{items.find(i => i.Id === parseInt(d.ItemId))?.Name || d.ItemName || '-'}</span>
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd6fe', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#5b21b6' }}>{d.Quantity || 0}</span>
                        ) : (
                          <TextField size="small" type="number" value={d.Quantity ?? ''} onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd6fe', textAlign: 'right', fontWeight: isViewMode ? 600 : 400 }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#5b21b6' }}>{d.Rate || 0}</span>
                        ) : (
                          <TextField size="small" type="number" value={d.Rate ?? ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd6fe', textAlign: 'center', fontWeight: isViewMode ? 600 : 400 }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#5b21b6' }}>{d.DiscountPercentage || 0}</span>
                        ) : (
                          <TextField size="small" type="number" value={d.DiscountPercentage ?? 0} onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, max: 100, style: { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd6fe', textAlign: 'right', fontWeight: isViewMode ? 600 : 400 }}>
                        {isViewMode ? (
                          <span style={{ fontWeight: 600, color: '#5b21b6' }}>{d.DiscountAmount || 0}</span>
                        ) : (
                          <TextField size="small" type="number" value={d.DiscountAmount ?? 0} onChange={(e) => handleDetailChange(index, 'DiscountAmount', e.target.value)} disabled={isViewMode} onFocus={(e) => e.target.select()} inputProps={{ min: 0, style: { textAlign: 'right', padding: '4px', fontSize: '0.75rem' } }} sx={{ width: '100%' }} />
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd6fe', textAlign: 'center', fontWeight: 600, color: '#7c3aed' }}>{d.VATRate || d.VATRate || 0}%</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd6fe', textAlign: 'right', fontWeight: 600, color: '#5b21b6' }}>{formatCurrency(d.TaxableAmount)}</td>
                      <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd6fe', textAlign: 'right', fontWeight: 600, color: '#7c3aed' }}>{formatCurrency(d.TotalAmount)}</td>
                      {!isViewMode && (
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #ddd6fe', textAlign: 'center' }}>
                          <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.25, color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                        </td>
                      )}
                    </tr>
                  ))}
                  {saleDetails.length === 0 && (
                    <tr><td colSpan={isViewMode ? 9 : 10} style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: isViewMode ? '#f1f5f9' : '#f5f3ff' }}>No items added</td></tr>
                  )}
                </tbody>
              </table>
            </Box>
          )}

          {/* Mobile Card View */}
          {isMobile && (
            <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 1 }}>
              {saleDetails.map((d, index) => (
                <Box key={index} sx={{ border: '1px solid #ddd6fe', borderRadius: 2, p: 1.5, mb: 1.5, bgcolor: '#f5f3ff' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#5b21b6', fontSize: '0.8rem' }}>
                      {items.find(i => i.Id === parseInt(d.ItemId))?.Name || 'Select Item'}
                    </Typography>
                    {!isViewMode && (
                      <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.5, bgcolor: '#fef2f2', color: '#dc2626' }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#7c3aed', fontSize: '0.65rem', fontWeight: 600 }}>Batch</Typography>
                      <TextField size="small" value={d.BatchNumber || ''} onChange={(e) => handleDetailChange(index, 'BatchNumber', e.target.value)} disabled={isViewMode} fullWidth sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#7c3aed', fontSize: '0.65rem', fontWeight: 600 }}>Qty</Typography>
                      <TextField size="small" type="number" value={d.Quantity || ''} onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} disabled={isViewMode} fullWidth inputProps={{ min: 0 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' } }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#7c3aed', fontSize: '0.65rem', fontWeight: 600 }}>Rate</Typography>
                      <TextField size="small" type="number" value={d.Rate || ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} disabled={isViewMode} fullWidth inputProps={{ min: 0 }} sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right' } }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #ddd6fe' }}>
                    <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 600 }}>Total</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#7c3aed' }}>{formatCurrency(d.TotalAmount)}</Typography>
                  </Box>
                </Box>
              ))}
              {saleDetails.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4, color: '#7c3aed', bgcolor: '#f5f3ff', borderRadius: 1, border: '1px dashed #ddd6fe' }}><Typography variant="body2">No items added</Typography></Box>
              )}
            </Box>
          )}

          <Divider sx={{ my: 0.5 }} />

          {/* Totals Section */}
          <Grid container spacing={1}>
            <Grid item xs={12} sm={8}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#7c3aed', mb: 0.5, display: 'block' }}>Additional Charges</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f5f3ff', p: 1, borderRadius: 1 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="Freight" type="number" value={formData.FreightAmount || ''} onChange={(e) => handleChange('FreightAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="Packing" type="number" value={formData.PackingAmount || ''} onChange={(e) => handleChange('PackingAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>

                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="Total Amount" type="number" value={formData.GrandTotal || ''} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>

                  {/* Payment Fields Row */}
                  {(customerAdvanceAmount > 0 || (parseFloat(formData.AdvanceAmount) || 0) > 0) && (
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="Advance Amount" type="number" value={formData.AdvanceAmount || ''} onChange={(e) => handleChange('AdvanceAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, max: customerAdvanceAmount, style: { textAlign: 'right' } }} sx={{ ...compactInput, '& .MuiOutlinedInput-root': { backgroundColor: '#ecfdf5' } }} />
                  </Grid>
                  )}
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="Cash Amount" type="number" value={formData.CashAmount || ''} onChange={(e) => handleChange('CashAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="UPI Amount" type="number" value={formData.UPIAmount || ''} onChange={(e) => handleChange('UPIAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField fullWidth size="small" label="Card Amount" type="number" value={formData.CardAmount || ''} onChange={(e) => handleChange('CardAmount', e.target.value)} disabled={isViewMode} inputProps={{ min: 0, style: { textAlign: 'right' } }} sx={compactInput} />
                  </Grid>

                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''} onChange={(e) => handleChange('Remarks', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  {/* Bank Selection - Show only when UPI or Card amount is entered */}
                  <Grid item xs={12} sm={8} md={4}>
                    {((parseFloat(formData.UPIAmount) || 0) > 0 || (parseFloat(formData.CardAmount) || 0) > 0) ? (
                      <FormControl fullWidth size="small" sx={{
                        ...compactInput,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#7c3aed',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#7c3aed',
                            borderWidth: '2px',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#7c3aed',
                          fontWeight: 500,
                          '&.Mui-focused': {
                            color: '#7c3aed',
                          }
                        }
                      }}>
                        <InputLabel>Bank *</InputLabel>
                        <Select
                          value={formData.BankId || ''}
                          onChange={(e) => handleChange('BankId', e.target.value)}
                          disabled={isViewMode || loading}
                          label="Bank *"
                          sx={{ fontSize: '0.8rem' }}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem' } } } }}
                        >
                          {loading ? (
                            <MenuItem value="" disabled sx={{ fontSize: '0.85rem', py: 0.5 }}>
                              Loading banks...
                            </MenuItem>
                          ) : banks && banks.length > 0 ? (
                            banks.map((bank) => (
                              <MenuItem key={bank.MasterId || bank.Id} value={bank.MasterId || bank.Id} sx={{ fontSize: '0.85rem', py: 0.5 }}>
                                {bank.Name || bank.MasterName}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled sx={{ fontSize: '0.85rem', py: 0.5 }}>
                              No banks available
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box sx={{
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Typography variant="caption" sx={{
                          color: '#9ca3af',
                          fontSize: '0.75rem',
                          fontStyle: 'italic'
                        }}>
                          Bank field appears for UPI/Card
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Payment Terms" value={formData.PaymentTerms || ''} onChange={(e) => handleChange('PaymentTerms', e.target.value)} disabled={isViewMode} inputProps={{ maxLength: 150 }} sx={compactInput} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Terms of Delivery" value={formData.TermsofDelivery || ''} onChange={(e) => handleChange('TermsofDelivery', e.target.value)} disabled={isViewMode} inputProps={{ maxLength: 150 }} sx={compactInput} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Reference Number" value={formData.ReferenceNumber || ''} onChange={(e) => handleChange('ReferenceNumber', e.target.value)} disabled={isViewMode} inputProps={{ maxLength: 150 }} sx={compactInput} />
                  </Grid>

                  {/* Audit Info - under Remarks */}
                  {isViewMode && (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                        Created by <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: '#7c3aed' }}>{formData.CreatedByName || '-'}</Typography> on{' '}
                        <Typography component="span" variant="caption" sx={{ color: '#3b82f6' }}>
                          {formData.CreatedDate ? new Date(formData.CreatedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(formData.CreatedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                        Modified by <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: '#16a34a' }}>{formData.ModifiedByName || '-'}</Typography> on{' '}
                        <Typography component="span" variant="caption" sx={{ color: '#15803d' }}>
                          {formData.ModifiedDate ? new Date(formData.ModifiedDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ', ' + new Date(formData.ModifiedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                        </Typography>
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Related Items Section */}
              {relatedItems.length > 0 && (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#7c3aed', mb: 0.5, display: 'block' }}>
                    Related Items (Product Components) - {relatedItems.length} product(s)
                  </Typography>
                  <Box sx={{ bgcolor: '#faf5ff', p: 1, borderRadius: 1, mb: 1, border: '1px solid #e9d5ff' }}>
                    {relatedItemsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                        <CircularProgress size={20} sx={{ color: '#7c3aed' }} />
                        <Typography variant="body2" sx={{ ml: 1.5, color: '#6b7280', fontSize: '0.75rem' }}>
                          Loading related items...
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {relatedItems.map((productGroup, groupIndex) => {
                          console.log('Rendering product group:', groupIndex, productGroup);
                          return (
                            <Box key={groupIndex} sx={{ mb: groupIndex < relatedItems.length - 1 ? 2 : 0 }}>
                              {/* Product Header */}
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                                pb: 0.5,
                                borderBottom: '2px solid #e9d5ff'
                              }}>
                                <Chip
                                  label="Product"
                                  size="small"
                                  sx={{
                                    fontSize: '0.6rem',
                                    height: 18,
                                    bgcolor: '#dbeafe',
                                    color: '#2563eb',
                                    fontWeight: 600,
                                    border: '1.5px solid #7c3aed',
                                    borderRadius: '12px'
                                  }}
                                />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.8rem' }}>
                                  {productGroup.productName}
                                </Typography>
                                <Chip
                                  label={`Qty: ${productGroup.productQuantity}`}
                                  size="small"
                                  sx={{
                                    fontSize: '0.6rem',
                                    height: 16,
                                    bgcolor: '#ddd6fe',
                                    color: '#7c3aed',
                                    fontWeight: 700
                                  }}
                                />
                                <Chip
                                  label={`${productGroup.items.length} components`}
                                  size="small"
                                  sx={{
                                    fontSize: '0.6rem',
                                    height: 16,
                                    bgcolor: '#f3e8ff',
                                    color: '#7c3aed',
                                    fontWeight: 600
                                  }}
                                />
                              </Box>

                              {/* Related Items Table */}
                              {productGroup.items.length > 0 ? (
                                <Box sx={{ border: '1px solid #e9d5ff', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
                                    <thead>
                                      <tr style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', height: '28px' }}>
                                        <th style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '28%', fontSize: '0.65rem' }}>Item Name</th>
                                        <th style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>Qty</th>
                                        <th style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem' }}>Balance Stock</th>
                                        <th style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>Status</th>
                                        <th style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem' }}>Rate</th>
                                        <th style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem' }}>Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {productGroup.items.map((relatedItem, index) => {
                                        const quantity = parseFloat(relatedItem.Quantity || relatedItem.Qty || 0);
                                        const rate = parseFloat(relatedItem.Rate || relatedItem.SaleRate || 0);
                                        const amount = quantity * rate;
                                        
                                        // Get balance stock from items array
                                        const componentItem = items.find(i => i.Id === (relatedItem.ItemId || relatedItem.ProductItemId));
                                        const balanceStock = componentItem?.BalanceStock || 0;
                                        const hasEnoughStock = balanceStock >= quantity;
                                        const stockShortage = quantity - balanceStock;

                                        return (
                                          <tr key={relatedItem.Id || index} style={{
                                            borderBottom: index < productGroup.items.length - 1 ? '1px solid #f3e8ff' : 'none',
                                            background: hasEnoughStock ? (index % 2 === 0 ? '#fff' : '#faf5ff') : '#fef2f2'
                                          }}>
                                            <td style={{ padding: '5px 8px', fontWeight: 500, fontSize: '0.7rem', color: '#1e293b' }}>
                                              {relatedItem.ProductChildName || relatedItem.ProductChildName || '-'}
                                            </td>
                                            <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                              {isViewMode ? (
                                                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#7c3aed' }}>
                                                  {quantity.toFixed(2)}
                                                </span>
                                              ) : (
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  min="0"
                                                  value={quantity}
                                                  onChange={(e) => handleRelatedItemQtyChange(groupIndex, index, e.target.value)}
                                                  style={{
                                                    width: '60px',
                                                    padding: '2px 4px',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    color: '#7c3aed',
                                                    textAlign: 'center',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '4px',
                                                    outline: 'none',
                                                    backgroundColor: '#fff'
                                                  }}
                                                  onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                                                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                                />
                                              )}
                                            </td>
                                            <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.7rem', color: hasEnoughStock ? '#059669' : '#dc2626' }}>
                                              {balanceStock.toFixed(2)}
                                            </td>
                                            <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                              {hasEnoughStock ? (
                                                <span style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: '2px',
                                                  fontSize: '0.65rem',
                                                  fontWeight: 600,
                                                  color: '#059669',
                                                  backgroundColor: '#d1fae5',
                                                  padding: '2px 6px',
                                                  borderRadius: '4px',
                                                  border: '1px solid #6ee7b7'
                                                }}>
                                                  <CheckCircle style={{ fontSize: '10px' }} />
                                                  OK
                                                </span>
                                              ) : (
                                                <span style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: '2px',
                                                  fontSize: '0.65rem',
                                                  fontWeight: 600,
                                                  color: '#dc2626',
                                                  backgroundColor: '#fee2e2',
                                                  padding: '2px 6px',
                                                  borderRadius: '4px',
                                                  border: '1px solid #fca5a5'
                                                }} title={`Short by ${stockShortage.toFixed(2)}`}>
                                                  <Warning style={{ fontSize: '10px' }} />
                                                  Low
                                                </span>
                                              )}
                                            </td>
                                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', color: '#1e40af' }}>
                                              AED {rate.toFixed(2)}
                                            </td>
                                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, fontSize: '0.75rem', color: '#7c3aed' }}>
                                              AED {amount.toFixed(2)}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </Box>
                              ) : (
                                <Box sx={{
                                  textAlign: 'center',
                                  py: 2,
                                  bgcolor: '#fff',
                                  borderRadius: 1,
                                  border: '1px dashed #e9d5ff'
                                }}>
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                    No related items found
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </>
              )}
              {/* Dispatch Details Section */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#7c3aed', mb: 0.5, mt: 1, display: 'block' }}>Dispatch Details</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f5f3ff', p: 1, borderRadius: 1, mb: 1 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={5}>
                    <TextField fullWidth size="small" label="Dispatch From" value={formData.DispatchFrom || ''} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField fullWidth size="small" label="Dispatch Location" value={formData.DispatchLocation || ''} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField fullWidth size="small" label="Dispatch Pincode" value={formData.DispatchPincode || ''} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Dispatch Address 1" value={formData.DispatchAddress1 || ''} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Dispatch Address 2" value={formData.DispatchAddress2 || ''} disabled sx={{ ...compactInput, ...disabledFieldStyle }} />
                  </Grid>
                </Grid>
              </Box>

              {/* Shipping Details Section */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#7c3aed', mb: 0.5, display: 'block' }}>Shipping Details</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f5f3ff', p: 1, borderRadius: 1, mb: 1 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4.5}>
                    <TextField fullWidth size="small" label="Ship To" value={formData.ShipTo || ''} onChange={(e) => handleChange('ShipTo', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  <Grid item xs={12} sm={2.5}>
                    <TextField fullWidth size="small" label="Ship VAT No" value={formData.ShipGSTNo || ''} onChange={(e) => handleChange('ShipGSTNo', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField fullWidth size="small" label="Ship Location" value={formData.ShipToLocation || ''} onChange={(e) => handleChange('ShipToLocation', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField fullWidth size="small" label="Ship Pincode" value={formData.ShipToPincode || ''} onChange={(e) => handleChange('ShipToPincode', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Ship To Address 1" value={formData.ShipToAddress1 || ''} onChange={(e) => handleChange('ShipToAddress1', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Ship To Address 2" value={formData.ShipToAddress2 || ''} onChange={(e) => handleChange('ShipToAddress2', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                </Grid>
              </Box>

              {/* Terms & Conditions Display - View Mode: collapsible labels */}
              {isViewMode && formData.SelectedTermsData && formData.SelectedTermsData.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {/* Local T&C - View Mode */}
                  {formData.SelectedTermsData.filter(t => !t.IsInternational).length > 0 && (
                    <Box sx={{ bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #fde68a', mb: 1 }}>
                      <Box 
                        onClick={() => setTcExpanded(!tcExpanded)}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, cursor: 'pointer', '&:hover': { bgcolor: '#fef3c7' }, borderRadius: 1 }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', fontSize: '0.8rem' }}>
                          📋 Terms & Conditions ({formData.SelectedTermsData.filter(t => !t.IsInternational).length})
                        </Typography>
                        {tcExpanded ? <ExpandLess sx={{ fontSize: 18, color: '#92400e' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#92400e' }} />}
                      </Box>
                      <Collapse in={tcExpanded}>
                        <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {formData.SelectedTermsData.filter(t => !t.IsInternational).map((term, index) => (
                            <Box key={term.Id || index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                              <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700, minWidth: 16 }}>
                                {index + 1}.
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.75rem', lineHeight: 1.4 }}>
                                {term.Name}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                  {/* International T&C - View Mode */}
                  {formData.SelectedTermsData.filter(t => t.IsInternational).length > 0 && (
                    <Box sx={{ bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #93c5fd' }}>
                      <Box 
                        onClick={() => setTcExpanded(!tcExpanded)}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, cursor: 'pointer', '&:hover': { bgcolor: '#dbeafe' }, borderRadius: 1 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.8rem' }}>
                            📋 International T&C ({formData.SelectedTermsData.filter(t => t.IsInternational).length})
                          </Typography>
                          <Chip label="International" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: '#1e40af', color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
                        </Box>
                        {tcExpanded ? <ExpandLess sx={{ fontSize: 18, color: '#1e40af' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#1e40af' }} />}
                      </Box>
                      <Collapse in={tcExpanded}>
                        <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {formData.SelectedTermsData.filter(t => t.IsInternational).map((term, index) => (
                            <Box key={term.Id || index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                              <Typography variant="caption" sx={{ color: '#1d4ed8', fontWeight: 700, minWidth: 16 }}>
                                {index + 1}.
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#1e3a5f', fontSize: '0.75rem', lineHeight: 1.4 }}>
                                {term.Name}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              )}

              {/* Terms & Conditions - Edit/Create Mode: collapsible checkboxes */}
              {!isViewMode && (
                <Box sx={{ mt: 1, bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #fde68a' }}>
                  <Box 
                    onClick={() => setTcExpanded(!tcExpanded)}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, cursor: 'pointer', '&:hover': { bgcolor: '#fef3c7' }, borderRadius: 1 }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', fontSize: '0.8rem' }}>
                      📋 Terms & Conditions {tcData.length > 0 ? `(${selectedTerms.length}/${tcData.length})` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {tcData.length > 0 && (
                        <Button
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleSelectAllTerms(); }}
                          sx={{ fontSize: '0.65rem', textTransform: 'none', color: '#d97706', minWidth: 0, py: 0, px: 0.5 }}
                        >
                          {selectedTerms.length === tcData.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      )}
                      {tcExpanded ? <ExpandLess sx={{ fontSize: 18, color: '#92400e' }} /> : <ExpandMore sx={{ fontSize: 18, color: '#92400e' }} />}
                    </Box>
                  </Box>
                  <Collapse in={tcExpanded}>
                    <Box sx={{ px: 1.5, pb: 1.5 }}>
                      {tcLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                          <CircularProgress size={16} sx={{ color: '#d97706' }} />
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Loading...</Typography>
                        </Box>
                      ) : tcData.length === 0 ? (
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>No terms available</Typography>
                      ) : (
                        <>
                          {/* T&C Search */}
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Search terms & conditions..."
                            value={tcSearchValue}
                            onChange={(e) => setTcSearchValue(e.target.value)}
                            sx={{ mb: 1, '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Search sx={{ fontSize: 16, color: '#9ca3af' }} />
                                </InputAdornment>
                              )
                            }}
                          />

                          {/* Local T&C */}
                          {tcData.filter(t => !t.IsInternational).length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', fontSize: '0.75rem' }}>
                                  Terms & Conditions ({tcData.filter(t => !t.IsInternational).filter(t => selectedTerms.includes(t.Id)).length}/{tcData.filter(t => !t.IsInternational).length})
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 150, overflow: 'auto' }}>
                                {tcData.filter(t => !t.IsInternational).filter(t => !tcSearchValue || t.Name?.toLowerCase().includes(tcSearchValue.toLowerCase())).map((term, index) => {
                                  const isSelected = selectedTerms.includes(term.Id);
                                  return (
                                    <Box
                                      key={term.Id}
                                      onClick={() => handleToggleTerm(term.Id)}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        py: 0.5,
                                        px: 1,
                                        borderRadius: 0.5,
                                        cursor: 'pointer',
                                        bgcolor: isSelected ? '#fef3c7' : 'transparent',
                                        border: isSelected ? '1px solid #f59e0b' : '1px solid transparent',
                                        '&:hover': { bgcolor: isSelected ? '#fde68a' : '#fefce8' }
                                      }}
                                    >
                                      <Checkbox
                                        checked={isSelected}
                                        size="small"
                                        sx={{ p: 0, color: '#d97706', '&.Mui-checked': { color: '#d97706' } }}
                                      />
                                      <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.75rem', lineHeight: 1.4 }}>
                                        {index + 1}. {term.Name}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                                {tcData.filter(t => !t.IsInternational).filter(t => !tcSearchValue || t.Name?.toLowerCase().includes(tcSearchValue.toLowerCase())).length === 0 && tcSearchValue && (
                                  <Typography variant="caption" sx={{ color: '#9ca3af', py: 0.5, px: 1 }}>No matching terms</Typography>
                                )}
                              </Box>
                            </Box>
                          )}

                          {/* International T&C */}
                          {tcData.filter(t => t.IsInternational).length > 0 && (
                            <Box sx={{ p: 1.5, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #93c5fd' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                                    International T&C ({tcData.filter(t => t.IsInternational).filter(t => selectedTerms.includes(t.Id)).length}/{tcData.filter(t => t.IsInternational).length})
                                  </Typography>
                                  <Chip label="International" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: '#1e40af', color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 150, overflow: 'auto' }}>
                                {tcData.filter(t => t.IsInternational).filter(t => !tcSearchValue || t.Name?.toLowerCase().includes(tcSearchValue.toLowerCase())).map((term, index) => {
                                  const isSelected = selectedTerms.includes(term.Id);
                                  return (
                                    <Box
                                      key={term.Id}
                                      onClick={() => handleToggleTerm(term.Id)}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        py: 0.5,
                                        px: 1,
                                        borderRadius: 0.5,
                                        cursor: 'pointer',
                                        bgcolor: isSelected ? '#dbeafe' : 'transparent',
                                        border: isSelected ? '1px solid #3b82f6' : '1px solid transparent',
                                        '&:hover': { bgcolor: isSelected ? '#bfdbfe' : '#eff6ff' }
                                      }}
                                    >
                                      <Checkbox
                                        checked={isSelected}
                                        size="small"
                                        sx={{ p: 0, color: '#1e40af', '&.Mui-checked': { color: '#1e40af' } }}
                                      />
                                      <Typography variant="caption" sx={{ color: '#1e3a5f', fontSize: '0.75rem', lineHeight: 1.4 }}>
                                        {index + 1}. {term.Name}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                                {tcData.filter(t => t.IsInternational).filter(t => !tcSearchValue || t.Name?.toLowerCase().includes(tcSearchValue.toLowerCase())).length === 0 && tcSearchValue && (
                                  <Typography variant="caption" sx={{ color: '#9ca3af', py: 0.5, px: 1 }}>No matching terms</Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              {/* Vehicle Details Section */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#7c3aed', mb: 0.5, display: 'block' }}>Vehicle Details</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f5f3ff', p: 1, borderRadius: 1, mb: 1 }}>
                <Grid container spacing={1}>
                  {/* Row 1: Vehicle Number and Distance */}

                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth size="small" label="Transporter Name" value={formData.TransporterName || ''} onChange={(e) => handleChange('TransporterName', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Vehicle Number" value={formData.VehicleNumber || ''} onChange={(e) => handleChange('VehicleNumber', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>


                  {/* Row 2: Transporter Name (wider) and Transporter VAT */}
                  <Grid item xs={12} sm={8}>
                    <TextField fullWidth size="small" label="Transporter VAT" value={formData.TransporterVAT || ''} onChange={(e) => handleChange('TransporterVAT', e.target.value)} disabled={isViewMode} sx={compactInput} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Distance (KM)" type="number" value={formData.Distance || ''} onChange={(e) => handleChange('Distance', e.target.value)} disabled={isViewMode} inputProps={{ min: 0 }} sx={compactInput} />
                  </Grid>
                </Grid>
              </Box>

              <Typography variant="caption" sx={{ fontWeight: 600, color: isViewMode ? '#475569' : '#7c3aed', mb: 0.5, display: 'block' }}>Summary</Typography>
              <Box sx={{ bgcolor: isViewMode ? '#f1f5f9' : '#f5f3ff', p: 1, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Subtotal</Typography><Typography variant="caption" sx={{ fontWeight: 700 }}>{formatCurrency(formData.TotalAmount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Discount</Typography><Typography variant="caption" sx={{ fontWeight: 700 }} color="error">-{formatCurrency(formData.DiscountAmount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Taxable</Typography><Typography variant="caption" sx={{ fontWeight: 700 }}>{formatCurrency(formData.TaxableAmount)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700 }}>VAT</Typography><Typography variant="caption" sx={{ fontWeight: 700 }}>{formatCurrency(formData.VATAmount || 0)}</Typography></Box>
                {(parseFloat(formData.FreightAmount) || 0) > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Freight</Typography><Typography variant="caption" sx={{ fontWeight: 700 }}>{formatCurrency(formData.FreightAmount)}</Typography></Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Freight VAT ({formData.FreightVATRate || 0}%)</Typography><Typography variant="caption" sx={{ fontWeight: 700 }}>{formatCurrency(formData.FreightVATAmount)}</Typography></Box>
                  </>
                )}
                {(parseFloat(formData.PackingAmount) || 0) > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Packing</Typography><Typography variant="caption" sx={{ fontWeight: 700 }}>{formatCurrency(formData.PackingAmount)}</Typography></Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Packing VAT ({formData.PackingVATRate || 0}%)</Typography><Typography variant="caption" sx={{ fontWeight: 700 }}>{formatCurrency(formData.PackingVATAmount)}</Typography></Box>
                  </>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" sx={{ fontWeight: 700 }}>Round Off</Typography><Typography variant="caption" sx={{ fontWeight: 700 }}>{formData.RoundOff >= 0 ? '+' : ''}{(formData.RoundOff || 0).toFixed(2)}</Typography></Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7c3aed' }}>{formatCurrency(formData.GrandTotal)}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total Paid</Typography><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3b82f6' }}>{formatCurrency((parseFloat(formData.CashAmount) || 0) + (parseFloat(formData.UPIAmount) || 0) + (parseFloat(formData.CardAmount) || 0) + (parseFloat(formData.AdvanceAmount) || 0))}</Typography></Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Balance</Typography><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#dc2626' }}>{formatCurrency(formData.BalanceAmount)}</Typography></Box>
              </Box>
            </Grid>
          </Grid>

        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1, borderTop: isViewMode ? '1px solid #e2e8f0' : '1px solid #ddd6fe', bgcolor: isViewMode ? '#f8fafc' : '#f5f3ff', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* PDF and WhatsApp buttons - show in view mode or after save */}
            {(isViewMode || formData.Id) && (
              <>
                <Button
                  onClick={handlePrintPDF}
                  variant="contained"
                  size="small"
                  startIcon={printLoading ? <CircularProgress size={16} color="inherit" /> : <Print />}
                  disabled={printLoading || !formData.BillNumber}
                  sx={{
                    bgcolor: '#1e40af',
                    color: '#fff',
                    '&:hover': { bgcolor: '#1e3a8a' },
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5
                  }}
                >
                  {printLoading ? 'Printing...' : 'Print'}
                </Button>
                <Button
                  onClick={handleWhatsAppShare}
                  variant="contained"
                  size="small"
                  startIcon={whatsappLoading ? <CircularProgress size={16} color="inherit" /> : <WhatsApp />}
                  disabled={whatsappLoading || !formData.BillNumber}
                  sx={{
                    bgcolor: '#25D366',
                    color: '#fff',
                    '&:hover': { bgcolor: '#1da851' },
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5
                  }}
                >
                  {whatsappLoading ? 'Sharing...' : 'Share WhatsApp'}
                </Button>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCancelClick} variant="outlined" size="small">Cancel</Button>
            {!isViewMode && (
              <Button 
                onClick={handleSaveWithValidation} 
                variant="contained" 
                size="small" 
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                Save
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* View All Items Modal */}
      <Dialog
        open={itemsModalOpen}
        onClose={handleViewAllItemsClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '90vh',
            position: 'relative',
            m: 1
          }
        }}
      >

        <DialogTitle sx={{ py: 1, px: 1.5, borderBottom: '1px solid #ddd6fe', bgcolor: '#f5f3ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#7c3aed', fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#5b21b6', fontSize: '0.9rem' }}>All Sale Items ({saleDetails.length})</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton onClick={handleViewAllItemsClose} size="small" sx={{ p: 0.4 }}><Close fontSize="small" /></IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem' }}>#</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', color: 'white', fontWeight: 600, fontSize: '0.7rem', minWidth: '200px' }}>Item Name</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '80px' }}>HSN</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '70px' }}>Qty</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '85px' }}>Rate</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '65px' }}>Disc%</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '60px' }}>VAT%</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '95px' }}>Taxable</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '95px' }}>Total</th>
                  {!isViewMode && <th style={{ padding: '8px 6px', textAlign: 'center', color: 'white', fontWeight: 600, fontSize: '0.7rem', width: '50px' }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {saleDetails.map((d, index) => (
                  <tr key={index} style={{ background: index % 2 === 0 ? '#f5f3ff' : '#ffffff', borderBottom: '1px solid #ddd6fe' }}>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 500, color: '#7c3aed', fontSize: '0.75rem' }}>{index + 1}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 500, color: '#5b21b6' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#5b21b6', fontSize: '0.75rem' }}>{items.find(i => i.Id === parseInt(d.ItemId))?.Name || d.ItemName || '-'}</span>
                      ) : (
                        <Select
                          fullWidth
                          size="small"
                          value={d.ItemId || ''}
                          onChange={(e) => handleItemSelect(index, e.target.value)}
                          sx={{
                            fontSize: '0.75rem',
                            minWidth: '180px',
                            '& .MuiSelect-select': { py: 0.4, fontSize: '0.75rem' }
                          }}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 200, '& .MuiMenuItem-root': { fontSize: '0.75rem', py: 0.4 } } } }}
                        >
                          <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>Select Item</MenuItem>
                          {items.map(item => {
                            const isAlreadySelected = saleDetails.some((detail, i) =>
                              i !== index && detail.ItemId === item.Id
                            );
                            return (
                              <MenuItem
                                key={item.Id}
                                value={item.Id}
                                disabled={isAlreadySelected}
                                sx={{
                                  fontSize: '0.75rem',
                                  py: 0.2,
                                  opacity: isAlreadySelected ? 0.5 : 1,
                                  color: isAlreadySelected ? '#9ca3af' : 'inherit'
                                }}
                              >
                                {item.Name} {isAlreadySelected && '(Already Added)'}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', color: '#6b7280', fontSize: '0.7rem' }}>{d.HSNCode || '-'}</td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#5b21b6' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#5b21b6', fontSize: '0.75rem' }}>{d.Quantity || 0}</span>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={d.Quantity ?? ''}
                          onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            min: 0,
                            style: { textAlign: 'center', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }}
                          sx={{
                            width: '65px',
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: '#5b21b6' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#5b21b6', fontSize: '0.75rem' }}>{formatCurrency(d.Rate)}</span>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={d.Rate ?? ''}
                          onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            min: 0,
                            style: { textAlign: 'right', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }}
                          sx={{
                            width: '80px',
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#7c3aed' }}>
                      {isViewMode ? (
                        <span style={{ fontWeight: 600, color: '#7c3aed', fontSize: '0.75rem' }}>{d.DiscountPercentage || 0}%</span>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={d.DiscountPercentage ?? 0}
                          onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          inputProps={{
                            min: 0,
                            max: 100,
                            style: { textAlign: 'center', padding: '3px', fontSize: '0.7rem' },
                            onKeyDown: (e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }
                          }}
                          sx={{
                            width: '60px',
                            '& .MuiOutlinedInput-root': { height: '28px' },
                            '& input': {
                              transition: 'none'
                            }
                          }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#7c3aed', fontSize: '0.75rem' }}>{d.VATRate || d.VATRate || 0}%</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: '#5b21b6', fontSize: '0.75rem' }}>{formatCurrency(d.TaxableAmount)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 700, color: '#7c3aed', fontSize: '0.75rem' }}>{formatCurrency(d.TotalAmount)}</td>
                    {!isViewMode && (
                      <td style={{ padding: '6px', textAlign: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => removeDetailRow(index)}
                          sx={{ p: 0.2, color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}
                        >
                          <Delete sx={{ fontSize: 14 }} />
                        </IconButton>
                      </td>
                    )}
                  </tr>
                ))}
                {saleDetails.length === 0 && (
                  <tr>
                    <td colSpan={isViewMode ? 9 : 10} style={{ padding: '16px', textAlign: 'center', color: '#6b7280', backgroundColor: '#f5f3ff', fontSize: '0.8rem' }}>
                      No items added
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot style={{ position: 'sticky', bottom: 0, background: 'linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%)' }}>
                <tr style={{ borderTop: '2px solid #7c3aed' }}>
                  <td colSpan="3" style={{ padding: '8px 6px', fontWeight: 700, textAlign: 'right', color: '#5b21b6', fontSize: '0.75rem' }}>Totals:</td>
                  <td style={{ padding: '8px 6px', textAlign: 'center', fontWeight: 700, color: '#7c3aed', fontSize: '0.75rem' }}>{saleDetails.reduce((sum, d) => sum + (parseFloat(d.Quantity) || 0), 0)}</td>
                  <td colSpan="3"></td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#5b21b6', fontSize: '0.75rem' }}>{formatCurrency(saleDetails.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0))}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: '#7c3aed', fontSize: '0.75rem' }}>{formatCurrency(saleDetails.reduce((sum, d) => sum + (parseFloat(d.TotalAmount) || 0), 0))}</td>
                  {!isViewMode && <td style={{ padding: '8px 6px' }}></td>}
                </tr>
              </tfoot>
            </table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 1.5, py: 1, borderTop: '1px solid #ddd6fe', bgcolor: '#f5f3ff', justifyContent: 'flex-end' }}>
          <Button onClick={handleViewAllItemsClose} variant="outlined" size="small" sx={{ fontSize: '0.75rem', py: 0.4, px: 1.5 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Validation Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '8px',
            m: isMobile ? 0 : 1,
            maxHeight: '80vh',
            width: isMobile ? '100%' : '85%',
            maxWidth: '1100px',
            minWidth: '850px'
          }
        }}
      >
        <DialogTitle sx={{
          py: 0.75,
          px: 1.5,
          borderBottom: '1px solid #d1d5db',
          bgcolor: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: '#fff',
          minHeight: '40px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <List sx={{ color: '#fff', fontSize: 16 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#041672ff', fontSize: '0.85rem' }}>
                Get Items ({getFilteredItems().length} available)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseGetItemsModal} size="small" sx={{ color: '#fff', p: 0.25 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {getItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2, color: '#6b7280', fontSize: '0.85rem' }}>Loading items...</Typography>
            </Box>
          ) : (
            <>
              {/* Search */}
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name, HSN code, or item code..."
                  value={getItemsSearchValue}
                  onChange={(e) => handleGetItemsSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 16, color: '#6b7280' }} />
                      </InputAdornment>
                    ),
                    endAdornment: getItemsSearchValue && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleGetItemsSearch('')}
                          sx={{ p: 0.25 }}
                        >
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' },
                    '& .MuiOutlinedInput-root': { bgcolor: '#fff' }
                  }}
                />
              </Box>

              {/* Selected Items Summary */}
              {selectedItems.length > 0 && (
                <Box sx={{ mb: 1.5, p: 1, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #bfdbfe' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                    Selected Items ({selectedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedItems.slice(0, 8).map(item => (
                      <Chip
                        key={item.Id}
                        label={`${item.Name} (${item.selectedQuantity})`}
                        size="small"
                        onDelete={() => handleItemSelection(item, false)}
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    ))}
                    {selectedItems.length > 8 && (
                      <Chip label={`+${selectedItems.length - 8} more`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    )}
                  </Box>
                </Box>
              )}

              {/* Items Table - Desktop */}
              {!isMobile && (
                <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                  <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', height: '32px' }}>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '4%', fontSize: '0.65rem' }}>
                            <Checkbox
                              size="small"
                              checked={selectedItems.length === getPaginatedItems().length && getPaginatedItems().length > 0}
                              indeterminate={selectedItems.length > 0 && selectedItems.length < getPaginatedItems().length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  getPaginatedItems().forEach(item => {
                                    if (!selectedItems.find(si => si.Id === item.Id)) {
                                      handleItemSelection(item, true);
                                    }
                                  });
                                } else {
                                  getPaginatedItems().forEach(item => {
                                    handleItemSelection(item, false);
                                  });
                                }
                              }}
                              sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' }, '& .MuiSvgIcon-root': { fontSize: 14 } }}
                            />
                          </th>
                          <th style={{ padding: '4px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '28%', fontSize: '0.65rem' }}>Item</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem' }}>HSN</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%', fontSize: '0.65rem' }}>VAT%</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '8%', fontSize: '0.65rem' }}>Stock</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '7%', fontSize: '0.65rem' }}>Qty</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.65rem' }}>Rate</th>
                          <th style={{ padding: '4px 3px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%', fontSize: '0.65rem' }}>Disc%</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '9%', fontSize: '0.65rem' }}>Disc Amt</th>
                          <th style={{ padding: '4px 3px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%', fontSize: '0.65rem' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedItems().map((item, index) => {
                          const selectedItem = selectedItems.find(si => si.Id === item.Id);
                          const isSelected = !!selectedItem;
                          const isAlreadyAdded = isItemAlreadyInSale(item.Id);
                          const isProduct = item.IsProduct === true || item.IsProduct === 1;

                          // Determine background color based on IsProduct flag
                          let backgroundColor;
                          if (isSelected) {
                            backgroundColor = '#eff6ff'; // Selected state (blue)
                          } else if (isProduct) {
                            backgroundColor = '#f5f3ff'; // Product items (light purple)
                          } else {
                            backgroundColor = index % 2 === 0 ? '#fff' : '#f9fafb'; // Alternating rows
                          }

                          return (
                            <tr key={item.Id} style={{
                              borderBottom: '1px solid #e5e7eb',
                              background: backgroundColor,
                              opacity: isAlreadyAdded ? 0.5 : 1
                            }}>
                              <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  disabled={isAlreadyAdded}
                                  onChange={(e) => handleItemSelection(item, e.target.checked)}
                                  sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }}
                                />
                              </td>
                              <td style={{ padding: '3px 8px', fontWeight: 500, fontSize: '0.75rem' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span>{item.Name}</span>
                                  {isProduct && (
                                    <Chip
                                      label="Product"
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem',
                                        height: 18,
                                        bgcolor: '#dbeafe',
                                        color: '#2563eb',
                                        fontWeight: 600,
                                        border: '1.5px solid #7c3aed',
                                        borderRadius: '12px',
                                        '& .MuiChip-label': {
                                          px: 1.25,
                                          py: 0
                                        }
                                      }}
                                    />
                                  )}
                                  {isAlreadyAdded && (
                                    <Chip label="Added" size="small" color="success" sx={{ fontSize: '0.6rem', height: 14 }} />
                                  )}
                                </Box>
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.7rem', color: '#6b7280' }}>{item.HSNCode || '-'}</td>
                              <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#1e40af' }}>{item.VATRate || item.VATRate || 0}%</td>
                              <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem', color: item.BalanceStock > 0 ? '#7c3aed' : '#dc2626' }}>
                                {item.BalanceStock || 0}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedQuantity}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                    inputProps={{ min: 1 }}
                                    sx={{
                                      width: 50,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'center',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>-</span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedRate}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedRate', e.target.value)}
                                    inputProps={{ min: 0 }}
                                    sx={{
                                      width: 65,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'right',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>
                                    {formatCurrency(item.SaleRate || 0)}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'center' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedDiscountPercentage}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountPercentage', e.target.value)}
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={{
                                      width: 45,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'center',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af', fontSize: '0.7rem' }}>0</span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'right' }}>
                                {isSelected ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={selectedItem.selectedDiscountAmount}
                                    onChange={(e) => handleSelectedItemChange(item.Id, 'selectedDiscountAmount', e.target.value)}
                                    inputProps={{ min: 0 }}
                                    sx={{
                                      width: 60,
                                      '& .MuiInputBase-input': {
                                        py: 0.2,
                                        px: 0.3,
                                        fontSize: '0.7rem',
                                        textAlign: 'right',
                                        height: '16px'
                                      },
                                      '& .MuiOutlinedInput-root': { height: '24px' }
                                    }}
                                  />
                                ) : (
                                  <span style={{ display: 'block', textAlign: 'right', color: '#9ca3af', fontSize: '0.7rem' }}>-</span>
                                )}
                              </td>
                              <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                                {isSelected ? (
                                  (() => {
                                    const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                    const rate = parseFloat(selectedItem.selectedRate) || 0;
                                    const discPct = parseFloat(selectedItem.selectedDiscountPercentage) || 0;
                                    const discAmt = parseFloat(selectedItem.selectedDiscountAmount) || 0;
                                    const amount = qty * rate;
                                    const finalDiscAmt = discAmt > 0 ? discAmt : (amount * discPct) / 100;
                                    const taxable = amount - finalDiscAmt;
                                    const gstAmt = (taxable * (item.VATRate || 0)) / 100;
                                    return formatCurrency(taxable + gstAmt);
                                  })()
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {getPaginatedItems().length === 0 && (
                          <tr>
                            <td colSpan="10" style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '0.8rem' }}>
                              {getItemsSearchValue ? 'No items found matching your search' : 'No items available'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              )}

              {/* Items Cards - Mobile */}
              {isMobile && (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {getPaginatedItems().map((item, index) => {
                    const selectedItem = selectedItems.find(si => si.Id === item.Id);
                    const isSelected = !!selectedItem;
                    const isAlreadyAdded = isItemAlreadyInSale(item.Id);
                    const isProduct = item.IsProduct === true || item.IsProduct === 1;

                    // Determine background color based on IsProduct flag
                    let backgroundColor;
                    if (isSelected) {
                      backgroundColor = '#eff6ff'; // Selected state (blue)
                    } else if (isProduct) {
                      backgroundColor = '#f5f3ff'; // Product items (light purple)
                    } else {
                      backgroundColor = '#fff'; // Default white
                    }

                    return (
                      <Box
                        key={item.Id}
                        sx={{
                          border: isSelected ? '2px solid #1e40af' : (isProduct ? '1px solid #7c3aed' : '1px solid #e5e7eb'),
                          borderRadius: 2,
                          p: 2,
                          mb: 1.5,
                          bgcolor: backgroundColor,
                          opacity: isAlreadyAdded ? 0.6 : 1,
                          boxShadow: isSelected ? '0 4px 12px rgba(30, 64, 175, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.2s ease',
                          position: 'relative'
                        }}
                        onClick={() => !isAlreadyAdded && handleItemSelection(item, !isSelected)}
                      >
                        {/* Selection Indicator */}
                        {isSelected && (
                          <Box sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: '#1e40af',
                            borderRadius: '50%',
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <CheckCircle sx={{ fontSize: 14, color: '#fff' }} />
                          </Box>
                        )}

                        {/* Already Added Badge */}
                        {isAlreadyAdded && (
                          <Box sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: '#7c3aed',
                            borderRadius: 1,
                            px: 1,
                            py: 0.25
                          }}>
                            <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 600 }}>
                              ADDED
                            </Typography>
                          </Box>
                        )}

                        {/* Item Info */}
                        <Box sx={{ pr: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="body1" sx={{
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              color: '#1e293b',
                              lineHeight: 1.3
                            }}>
                              {item.Name}
                            </Typography>
                            {isProduct && (
                              <Chip
                                label="Product"
                                size="small"
                                sx={{
                                  fontSize: '0.7rem',
                                  height: 22,
                                  bgcolor: '#dbeafe',
                                  color: '#2563eb',
                                  fontWeight: 600,
                                  border: '1.5px solid #7c3aed',
                                  borderRadius: '14px',
                                  '& .MuiChip-label': {
                                    px: 1.5,
                                    py: 0
                                  }
                                }}
                              />
                            )}
                          </Box>

                          {/* Stock and Rate Row */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                fontWeight: 500
                              }}>
                                Stock:
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                color: item.BalanceStock > 0 ? '#7c3aed' : '#dc2626',
                                bgcolor: item.BalanceStock > 0 ? '#f5f3ff' : '#fef2f2',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1
                              }}>
                                {item.BalanceStock || 0}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{
                                fontSize: '0.75rem',
                                color: '#6b7280',
                                fontWeight: 500
                              }}>
                                Rate:
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: '#1e40af',
                                bgcolor: '#eff6ff',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1
                              }}>
                                {formatCurrency(item.SaleRate || 0)}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Additional Info Row */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{
                              fontSize: '0.7rem',
                              color: '#9ca3af',
                              bgcolor: '#f3f4f6',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1
                            }}>
                              HSN: {item.HSNCode || 'N/A'}
                            </Typography>

                            <Typography sx={{
                              fontSize: '0.7rem',
                              color: '#7c3aed',
                              bgcolor: '#f3e8ff',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              fontWeight: 600
                            }}>
                              GST: {item.VATRate || 0}%
                            </Typography>
                          </Box>

                          {/* Quantity Input - Only show when selected */}
                          {isSelected && (
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e5e7eb' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: '#374151',
                                  minWidth: '60px'
                                }}>
                                  Quantity:
                                </Typography>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={selectedItem.selectedQuantity}
                                  onChange={(e) => handleSelectedItemChange(item.Id, 'selectedQuantity', e.target.value)}
                                  inputProps={{ min: 1 }}
                                  sx={{
                                    flex: 1,
                                    '& .MuiInputBase-input': {
                                      fontSize: '0.8rem',
                                      textAlign: 'center',
                                      fontWeight: 600,
                                      color: '#1e40af'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: '#fff',
                                      '&:hover': { borderColor: '#1e40af' },
                                      '&.Mui-focused': { borderColor: '#1e40af' }
                                    }
                                  }}
                                />
                              </Box>

                              {/* Total Display */}
                              <Box sx={{
                                bgcolor: '#1e40af',
                                borderRadius: 1,
                                p: 1,
                                textAlign: 'center'
                              }}>
                                <Typography sx={{
                                  fontSize: '0.7rem',
                                  color: '#bfdbfe',
                                  mb: 0.25
                                }}>
                                  Total Amount
                                </Typography>
                                <Typography sx={{
                                  fontSize: '0.9rem',
                                  fontWeight: 700,
                                  color: '#fff'
                                }}>
                                  {(() => {
                                    const qty = parseFloat(selectedItem.selectedQuantity) || 0;
                                    const rate = parseFloat(selectedItem.selectedRate) || 0;
                                    const amount = qty * rate;
                                    const gstAmt = (amount * (item.VATRate || 0)) / 100;
                                    return formatCurrency(amount + gstAmt);
                                  })()}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}

                  {getPaginatedItems().length === 0 && (
                    <Box sx={{
                      textAlign: 'center',
                      py: 6,
                      color: '#6b7280',
                      bgcolor: '#f9fafb',
                      borderRadius: 2,
                      border: '1px dashed #d1d5db'
                    }}>
                      <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 1 }}>
                        {getItemsSearchValue ? '🔍 No items found' : '📦 No items available'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {getItemsSearchValue ? 'Try adjusting your search terms' : 'No items to display'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Pagination */}
              {getTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                  <Pagination
                    count={getTotalPages}
                    page={currentItemsPage}
                    onChange={(e, page) => setCurrentItemsPage(page)}
                    size="small"
                    color="primary"
                    sx={{ '& .MuiPaginationItem-root': { fontSize: '0.75rem' } }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 1.5, py: 1, borderTop: '1px solid #d1d5db', bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
            {selectedItems.length > 0 && `${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''} selected`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleCloseGetItemsModal}
              variant="outlined"
              size="small"
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedItems}
              variant="contained"
              size="small"
              disabled={selectedItems.length === 0 || addingItemsLoading}
              sx={{
                bgcolor: '#1e40af',
                '&:hover': { bgcolor: '#1d4ed8' },
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.5
              }}
            >
              {addingItemsLoading ? (
                <>
                  <CircularProgress size={14} sx={{ mr: 0.5 }} />
                  Adding...
                </>
              ) : (
                `Add Selected (${selectedItems.length})`
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleStayOnPage}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              backgroundColor: '#fef3c7',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Unsaved Changes
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            You have unsaved changes that will be lost if you leave this page.
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            Are you sure you want to exit without saving?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleStayOnPage}
            variant="outlined"
            sx={{
              borderColor: '#3b82f6',
              color: '#3b82f6',
              '&:hover': { borderColor: '#7c3aed', backgroundColor: '#f5f3ff' }
            }}
          >
            Stay on Page
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
          >
            Exit Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ledger Modal */}
      <Dialog
        open={ledgerModalOpen}
        onClose={handleCloseLedgerModal}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '8px',
            m: isMobile ? 0 : 1,
            maxHeight: '85vh',
            width: isMobile ? '100%' : '90%',
            maxWidth: '1200px'
          }
        }}
      >
        <DialogTitle sx={{
          py: 1,
          px: 2,
          borderBottom: '1px solid #d1d5db',
          bgcolor: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: '#fff'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <List sx={{ color: '#600404ff', fontSize: 18 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#520404ff' }}>
                Customer Ledger - {getCustomerDisplayName() || 'No Customer Selected'}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseLedgerModal} size="small" sx={{ color: '#fff' }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {/* Date Range Selection */}
          <Box sx={{ mb: 1.5, p: 1, bgcolor: '#fff', borderRadius: 1, border: '1px solid #e5e7eb' }}>

            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From Date"
                  value={ledgerFromDate}
                  onChange={(e) => setLedgerFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' },
                    '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                    '& .MuiInputBase-root': { minHeight: '32px' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To Date"
                  value={ledgerToDate}
                  onChange={(e) => setLedgerToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-input': { py: 0.5, fontSize: '0.75rem' },
                    '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                    '& .MuiInputBase-root': { minHeight: '32px' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  onClick={handleLedgerDateChange}
                  disabled={!ledgerFromDate || !ledgerToDate || ledgerLoading}
                  sx={{
                    bgcolor: '#1e40af',
                    '&:hover': { bgcolor: '#1d4ed8' },
                    fontSize: '0.7rem',
                    py: 0.5,
                    px: 1.5,
                    minHeight: '32px'
                  }}
                >
                  {ledgerLoading ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
                  Get Ledger
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Ledger Table */}
          {ledgerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
              <CircularProgress size={28} />
              <Typography variant="caption" sx={{ ml: 1.5, color: '#6b7280', fontSize: '0.75rem' }}>Loading ledger data...</Typography>
            </Box>
          ) : (
            <Box sx={{ border: '1px solid #d1d5db', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
              <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}>
                      <th style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '6%', fontSize: '0.7rem' }}>Sl No</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '10%', fontSize: '0.7rem' }}>Date</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '32%', fontSize: '0.7rem' }}>Description</th>
                      <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '13%', fontSize: '0.7rem' }}>Debit</th>
                      <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '13%', fontSize: '0.7rem' }}>Credit</th>
                      <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '14%', fontSize: '0.7rem' }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let runningBalance = 0;
                      return ledgerData.map((ledger, index) => {
                        const debitAmount = parseFloat(ledger.debit) || 0;
                        const creditAmount = parseFloat(ledger.credit) || 0;

                        // Check for special entries
                        const description = (ledger.description1 || '').toLowerCase();
                        const isTotal = description.includes('total');
                        const isClosingBalance = description.includes('closing balance');

                        // Calculate running balance for all entries except "Total"
                        if (!isTotal) {
                          runningBalance += creditAmount - debitAmount;
                        }

                        return (
                          <tr key={ledger.id || index} style={{
                            borderBottom: '1px solid #e5e7eb',
                            background: index % 2 === 0 ? '#fff' : '#f9fafb'
                          }}>
                            <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 500, color: '#6b7280', fontSize: '0.7rem' }}>
                              {index + 1}
                            </td>
                            <td style={{ padding: '4px 8px', fontWeight: 500, color: '#374151', fontSize: '0.7rem' }}>
                              {ledger.date1 ? new Date(ledger.date1).toLocaleDateString('en-IN') : '-'}
                            </td>
                            <td style={{ padding: '4px 8px', color: '#374151', fontSize: '0.7rem' }}>
                              {ledger.description1 || '-'}
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#dc2626', fontSize: '0.7rem' }}>
                              {debitAmount > 0 ? `AED ${debitAmount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600, color: '#7c3aed', fontSize: '0.7rem' }}>
                              {creditAmount > 0 ? `AED ${creditAmount.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                            </td>
                            <td style={{
                              padding: '4px 8px',
                              textAlign: 'right',
                              fontWeight: 700,
                              color: (isTotal || isClosingBalance) ? '#6b7280' : (runningBalance >= 0 ? '#7c3aed' : '#dc2626'),
                              backgroundColor: (isTotal || isClosingBalance) ? 'transparent' : (runningBalance >= 0 ? '#f5f3ff' : '#fef2f2'),
                              fontSize: '0.7rem'
                            }}>
                              {(isTotal || isClosingBalance) ? '-' : (
                                `AED ${Math.abs(runningBalance).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${runningBalance < 0 ? ' (Dr)' : ' (Cr)'}`
                              )}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                    {ledgerData.length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '0.75rem' }}>
                          {ledgerFromDate && ledgerToDate ? 'No ledger entries found for the selected date range' : 'Select date range to view ledger entries'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {ledgerData.length > 0 && (
                    <tfoot style={{ position: 'sticky', bottom: 0, background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)' }}>
                      <tr style={{ borderTop: '2px solid #1e40af' }}>
                        <td colSpan="5" style={{ padding: '5px 8px', fontWeight: 700, textAlign: 'right', color: '#374151', fontSize: '0.7rem' }}>
                          Closing Balance:
                        </td>
                        <td style={{
                          padding: '5px 8px',
                          textAlign: 'right',
                          fontWeight: 700,
                          color: '#fff',
                          fontSize: '0.7rem',
                          backgroundColor: (() => {
                            // Find the closing balance entry from the ledger data
                            const closingBalanceEntry = ledgerData.find(ledger =>
                              (ledger.description1 || '').toLowerCase().includes('closing balance')
                            );

                            if (closingBalanceEntry) {
                              const debitAmount = parseFloat(closingBalanceEntry.debit) || 0;
                              const creditAmount = parseFloat(closingBalanceEntry.credit) || 0;
                              const balance = creditAmount - debitAmount;
                              return balance >= 0 ? '#7c3aed' : '#dc2626';
                            }
                            return '#6b7280';
                          })()
                        }}>
                          {(() => {
                            // Find the closing balance entry from the ledger data
                            const closingBalanceEntry = ledgerData.find(ledger =>
                              (ledger.description1 || '').toLowerCase().includes('closing balance')
                            );

                            if (closingBalanceEntry) {
                              const debitAmount = parseFloat(closingBalanceEntry.debit) || 0;
                              const creditAmount = parseFloat(closingBalanceEntry.credit) || 0;
                              const balance = creditAmount - debitAmount;
                              return `AED ${Math.abs(balance).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${balance < 0 ? '(Dr)' : '(Cr)'}`;
                            }
                            return 'No closing balance found';
                          })()}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 1.5, py: 0.75, borderTop: '1px solid #d1d5db', bgcolor: '#f8fafc' }}>
          <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem', mr: 'auto' }}>
            {ledgerData.length > 0 && `${ledgerData.length} entries found`}
          </Typography>
          <Button onClick={handleCloseLedgerModal} variant="outlined" size="small" sx={{ py: 0.5, px: 1.5, fontSize: '0.7rem', minHeight: '28px' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add Client Modal */}
      <ClientModal
        open={clientModalOpen}
        onClose={handleCloseClientModal}
        modalType="client"
        modalAction="create"
        selectedItem={null}
        formData={clientFormData}
        setFormData={setClientFormData}
        onSave={handleSaveClient}
        loading={clientLoading}
      />

      {/* Quick Add Item Modal */}
      <ItemModal
        open={quickItemModalOpen}
        onClose={handleCloseQuickItemModal}
        onSave={handleQuickItemSaved}
        itemData={null}
        mode="create"
        categories={quickItemCategories}
        brands={quickItemBrands}
        uoms={quickItemUoms}
        existingItems={items}
      />
    </>
  );
};

export default SaleModal;

